import React, { useEffect, useState } from 'react';
import './ProfessionalPage.css';
import InternetFlowAnimation from './InternetFlowAnimation';
import ProgramLifecycle from './ProgramLifecycle';
import SystemDesignLifecycle from './SystemDesignLifecycle';
import HeroStats from './HeroStats';
import Diagrams from './Diagrams';
import PythonPlayground from './PythonPlayground';
import AssistantLogo from './AssistantLogo';
import { useInterview } from './useInterviewContext';
import { javaQAData } from '../Data/javaQAData';

const ProfessionalPage = ({ type, data }) => {
    const { toggleCoach, setActiveTopic } = useInterview();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('All');
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [activePlaygroundCode, setActivePlaygroundCode] = useState(null);
    const [mainTab, setMainTab] = useState('modules'); // 'modules' or 'qa'
    const [expandedQA, setExpandedQA] = useState({}); // Track expanded questions
    const [qaLangs, setQALangs] = useState({}); // Track language for each QA: { [id]: 'java' | 'python' }
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedQAs, setSelectedQAs] = useState(new Set());

    const handlePracticeWithAI = (topic) => {
        setActiveTopic({ ...topic, type });
        toggleCoach(true);
    };

    const config = {
        pdm: {
            title: "Product Management",
            subtitle: "Define Strategy, Drive Execution, deliver Value",
            categories: ["All", "Strategy", "Discovery", "Execution", "Analytics", "Growth", "Ethics", "Leadership", "Technical"],
            topics: [
                {
                    title: "Product Strategy (Vision)",
                    category: "Strategy",
                    desc: "Defining the 'Why' and 'What' before the 'How'. Mapping Mission to Roadmap.",
                    example: "Defined product vision for a 0-1 product, securing executive buy-in.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Mission (Forever)", color: "#a855f7" },
                            { label: "Vision (5-10 Years)", color: "#8b5cf6" },
                            { label: "Strategy (1-3 Years)", color: "#6366f1" },
                            { label: "Roadmap (Now-1 Year)", color: "#3b82f6" },
                            { label: "Execution (Sprints)", color: "#0ea5e9" }
                        ]
                    },
                    staticContent: [
                        "**Mission:** The north star. Why does the company exist?",
                        "**Vision:** The future world we are trying to create.",
                        "**Strategy:** The path we choose to get there (and what we choose NOT to do).",
                        "**The 3 Horizons:** H1 (Core Business), H2 (Emerging Opportunities), H3 (Long-term Bets)."
                    ]
                },
                {
                    title: "User Discovery & Design Thinking",
                    category: "Discovery",
                    desc: "Empathizing, defining, ideating, prototyping, and testing in rapid cycles.",
                    studyTips: "Fall in love with the problem, not the solution.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Empathize" },
                            { label: "Define" },
                            { label: "Ideate" },
                            { label: "Prototype" },
                            { label: "Test" }
                        ]
                    },
                    staticContent: [
                        "**The Mom Test:** Don't ask 'Is this a good idea?' (They will lie). Ask about their past behavior and problems.",
                        "**Double Diamond:** Diverge (explore many options) -> Converge (select the best) -> Diverge (develop) -> Converge (deliver).",
                        "**Customer Development:** Get out of the building. Your desk is a dangerous place to view the world.",
                        "**Jobs To Be Done (JTBD):** People don't buy a drill, they hire it to make a hole."
                    ]
                },
                {
                    title: "Prioritization (RICE)",
                    category: "Strategy",
                    desc: "Ruthlessly prioritizing features based on Reach, Impact, Confidence, and Effort.",
                    studyTips: "Prioritization is about saying 'No' to good ideas to focus on great ones.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Effort / Cost",
                        y: "Value / Impact",
                        quadrants: [
                            { label: "Quick Wins", color: "#22c55e", desc: "High Value / Low Effort" },
                            { label: "Big Bets", color: "#3b82f6", desc: "High Value / High Effort" },
                            { label: "Fill-ins", color: "#eab308", desc: "Low Value / Low Effort" },
                            { label: "Money Pit", color: "#ef4444", desc: "Low Value / High Effort" }
                        ]
                    },
                    staticContent: [
                        "**RICE Score:** (Reach * Impact * Confidence) / Effort.",
                        "**MoSCoW Method:** Must have, Should have, Could have, Won't have (this time).",
                        "**Kano Model:** Basics (Must-be), Performance (More is better), Delighters (Unexpected joy).",
                        "**Opportunity Cost:** Every hour spent on Feature A is an hour NOT learned on Feature B."
                    ]
                },
                {
                    title: "Product Analytics (AARRR)",
                    category: "Analytics",
                    desc: "The 'Pirate Metrics' framework: Acquisition, Activation, Retention, Referral, Revenue.",
                    studyTips: "Analyze the funnel to identify the biggest drop-off points.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Acquisition" },
                            { label: "Activation" },
                            { label: "Retention" },
                            { label: "Referral" },
                            { label: "Revenue" }
                        ],
                        centerText: "Growth"
                    },
                    staticContent: [
                        "**Acquisition:** How do users find us? (SEO, Ads, Viral).",
                        "**Activation:** Do they have a 'Happy First Experience'? (Sign up != Activation).",
                        "**Retention:** Do they come back? (The most important metric).",
                        "**Referral:** Do they tell others? (Viral Loop).",
                        "**Revenue:** How do we make money? (LTV vs CAC)."
                    ]
                },
                {
                    title: "Go-To-Market (GTM) Strategy",
                    category: "Execution",
                    desc: "Defining the launch plan, pricing, distribution channels, and messaging.",
                    studyTips: "Sync closely with sales and marketing teams for a unified launch.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Successful Launch",
                        pillars: [
                            { label: "Target Audience", desc: "Who?" },
                            { label: "Value Prop", desc: "Why Us?" },
                            { label: "Pricing", desc: "How much?" },
                            { label: "Distribution", desc: "Where?" }
                        ],
                        foundation: "Product Readiness"
                    },
                    staticContent: [
                        "**Positioning:** The distinct place you want to occupy in the customer's mind.",
                        "**Messaging:** The words used to communicate value. '1000 songs in your pocket'.",
                        "**Launch Tiers:** Alpha (Internal), Beta (Trusted Users), GA (General Availability).",
                        "**Sales Enablement:** Training the sales team so they can actually sell what you built."
                    ]
                },
                {
                    title: "Stakeholder Management",
                    category: "Execution",
                    desc: "Aligning divergent interests across sales, legal, engineering, and executives.",
                    studyTips: "Manage expectations: Under-promise and over-deliver.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Interest",
                        y: "Power / Influence",
                        quadrants: [
                            { label: "Keep Satisfied", color: "#eab308", desc: "High Power / Low Interest" },
                            { label: "Manage Closely", color: "#22c55e", desc: "High Power / High Interest" },
                            { label: "Monitor", color: "#94a3b8", desc: "Low Power / Low Interest" },
                            { label: "Keep Informed", color: "#3b82f6", desc: "Low Power / High Interest" }
                        ]
                    },
                    staticContent: [
                        "**Translation:** Speak 'Revenue' to Sales, 'Risk' to Legal, 'Debt' to Eng, 'Retention' to Execs.",
                        "**No Surprises:** Bad news must travel fast. Never let a stakeholder find out from someone else.",
                        "**Disagree and Commit:** You don't need consensus (everyone agrees), you need alignment (everyone supports).",
                        "**The 'HiPPO':** Highest Paid Person's Opinion. Counter it with data."
                    ]
                },
                {
                    title: "MVP vs MLP (Lovable Product)",
                    category: "Execution",
                    desc: "Finding the balance between the minimum viable and the minimum lovable set of features.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Delightful (MLP)", color: "#a855f7" },
                            { label: "Usable", color: "#8b5cf6" },
                            { label: "Reliable", color: "#6366f1" },
                            { label: "Functional (MVP)", color: "#3b82f6" }
                        ]
                    },
                    staticContent: [
                        "**MVP (Minimum Viable Product):** The smallest thing you can build to learn. (e.g., A landing page).",
                        "**MLP (Minimum Lovable Product):** The smallest thing you can build that users will adore and tell friends about.",
                        "**Riskiest Assumption Test (RAT):** Identify the biggest risk (Value, Usability, Feasibility, Viability) and test THAT.",
                        "**Feature Bloat:** More features != Better product. Complexity kills."
                    ]
                },
                {
                    title: "User Journey & Experience Mapping",
                    category: "Discovery",
                    desc: "Visualizing every touchpoint a user has with the product to find friction.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Awareness" },
                            { label: "Consideration" },
                            { label: "Purchase" },
                            { label: "Retention" },
                            { label: "Advocacy" }
                        ],
                        centerText: "Journey"
                    },
                    staticContent: [
                        "**Friction Logs:** Go through your own sign-up flow every month. Fix the annoyances.",
                        "**Service Blueprint:** Mapping the visible user actions AND the invisible backend/operational processes.",
                        "**Magic Moment:** The instant the user thinks 'Aha! This is valuable'. Minimize TTV (Time to Value).",
                        "**Pain vs Gain:** Users switch when the Pain of current solution > Cost of switching."
                    ]
                },
                {
                    title: "Agile/Scrum for Product Owners",
                    category: "Execution",
                    desc: "Managing backlogs, writing user stories, and facilitating sprint ceremonies.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Backlog Refinement" },
                            { label: "Sprint Planning" },
                            { label: "Daily Standup" },
                            { label: "Review" },
                            { label: "Retrospective" }
                        ]
                    },
                    staticContent: [
                        "**User Stories:** 'As a [User], I want to [Action], so that [Benefit]'.",
                        "**Acceptance Criteria:** The definition of done. Binary 'Yes/No' conditions.",
                        "**Velocity:** Not a performance metric, but a planning tool. How much SHOULD we commit to?",
                        "**Definition of Done (DoD):** Code written, reviewed, tested, documented, and deployed."
                    ]
                },
                {
                    title: "Product-Market Fit (PMF) Signals",
                    category: "Strategy",
                    desc: "Recognizing when your market is pulling the product out of your hands.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Satisfaction",
                        y: "Importance",
                        quadrants: [
                            { label: "Opportunity", color: "#22c55e", desc: "High Imp / Low Sat" },
                            { label: "Competitive", color: "#ef4444", desc: "High Imp / High Sat" },
                            { label: "Irrelevant", color: "#94a3b8", desc: "Low Imp / Low Sat" },
                            { label: "Overserved", color: "#eab308", desc: "Low Imp / High Sat" }
                        ]
                    },
                    staticContent: [
                        "**The 40% Rule:** Ask 'How disappointed would you be if you could no longer use this?' If >40% say 'Very', you have PMF.",
                        "**Pull vs Push:** You stop pushing the product; the market pulls it from you (server crashes, support overload).",
                        "**Retention Flattening:** Does the retention curve flatten out (good) or go to zero (bad)?",
                        "**NPS (Net Promoter Score):** Promoters (9-10) - Detractors (0-6). A lagging indicator."
                    ]
                },
                {
                    title: "Product Ethics & Inclusivity",
                    category: "Ethics",
                    desc: "Building products that prevent bias, ensure accessibility, and protect privacy.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Responsible Tech",
                        pillars: [
                            { label: "Fairness", desc: "Bias Mitigation" },
                            { label: "Privacy", desc: "User Control" },
                            { label: "Safety", desc: "Abuse Prevention" },
                            { label: "Accessibility", desc: "a11y / WCAG" }
                        ],
                        foundation: "Design Principles"
                    },
                    staticContent: [
                        "**Dark Patterns:** UI designed to trick users (e.g., hiding the cancel button). Don't do it.",
                        "**Algorithmic Bias:** Data is historical, and history is biased. AI learns that bias.",
                        "**Accessibility (a11y):** Designing for screen readers, color blindness, and keyboard navigation. It helps everyone.",
                        "**Tech Debt vs Ethics Debt:** Moving fast and breaking things is fine, until you break democracy or mental health."
                    ]
                },
                {
                    title: "Growth Loops",
                    category: "Growth",
                    desc: "Leveraging the product itself as the primary driver of user acquisition.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Trigger (External/Internal)" },
                            { label: "Action" },
                            { label: "Variable Reward" },
                            { label: "Investment" }
                        ],
                        centerText: "Hook"
                    },
                    staticContent: [
                        "**The Hook Model:** Trigger -> Action -> Variable Reward -> Investment (Nir Eyal).",
                        "**Viral Coefficient (K-factor):** Number of new users each existing user invites. If K > 1, exponential growth.",
                        "**Loops vs Funnels:** Funnels have a start and end. Loops feed themselves (e.g., Pinterest: User pins -> SEO -> New User).",
                        "**Network Effects:** The product gets better the more people use it (Facebook, Slack, Uber)."
                    ]
                },
                {
                    title: "Retention & Churn Analysis",
                    category: "Analytics",
                    desc: "Solving the 'leaky bucket' problem by keeping users engaged long-term.",
                    studyTips: "Retention is the best measure of product-market fit.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "New User" },
                            { label: "Current User" },
                            { label: "Resurrected User" },
                            { label: "Churned User" }
                        ],
                        centerText: "States"
                    },
                    staticContent: [
                        "**Cohorts:** Group users by start date. Are newer cohorts retaining better than older ones?",
                        "**Power Users:** The top 1% who use the product daily. Study them. What do they do differently?",
                        "**Churn:** Voluntary (I quit) vs Involuntary (Credit card failed).",
                        "**L-ness:** L5/7 = Used 5 days out of last 7. sticky."
                    ]
                },
                {
                    title: "Pricing Models",
                    category: "Strategy",
                    desc: "Freemium, subscription, tiered pricing, and value-based pricing strategies.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Price Sensitivity",
                        y: "Value Perception",
                        quadrants: [
                            { label: "Premium", color: "#a855f7", desc: "High Value / Low Sens" },
                            { label: "Economy", color: "#22c55e", desc: "Low Value / High Sens" },
                            { label: "Penetration", color: "#3b82f6", desc: "High Value / High Sens" },
                            { label: "Skimming", color: "#eab308", desc: "Low Value / Low Sens" }
                        ]
                    },
                    staticContent: [
                        "**Freemium:** Free for most, paid for power users. High support cost, huge funnel.",
                        "**Value-Based Pricing:** Charge based on the value you create, not the cost to build.",
                        "**Tiered Pricing:** Good, Better, Best. Uses 'Decoy Effect' to nudge users to the middle.",
                        "**Usage-Based:** Tax on success (AWS, Stripe). Align incentives perfectly."
                    ]
                },
                {
                    title: "Technical PM Foundations",
                    category: "Technical",
                    desc: "Understanding APIs, Latency, Data structures, and technical feasibility.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Feasibility",
                        pillars: [
                            { label: "APIs", desc: "Communication" },
                            { label: "Data", desc: "SQL / NoSQL" },
                            { label: "Client/Server", desc: "Architecture" },
                            { label: "Cloud", desc: "Scale / Infra" }
                        ],
                        foundation: "CS Basics"
                    },
                    staticContent: [
                        "**API (Application Programming Interface):** The menu at a restaurant. You ask for food (Request), kitchen cooks it (Server), waiter brings it (Response).",
                        "**Latency:** Speed. Time to first byte.",
                        "**SQL:** Structured data (Excel). **NoSQL:** Unstructured data (Docs).",
                        "**Technical Debt:** Taking a shortcut now to ship faster, but paying 'interest' (slower changes) later."
                    ]
                },
                {
                    title: "Product Sense (Ambiguity)",
                    category: "Strategy",
                    desc: "Deconstructing ambiguous problems into actionable product solutions.",
                    example: "Designed a new feature for a 2-sided marketplace to reduce fraud.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Comprehend (The Goal)" },
                            { label: "Identify (Users)" },
                            { label: "Report (Needs)" },
                            { label: "Cut (Prioritize)" },
                            { label: "List (Solutions)" },
                            { label: "Evaluate" },
                            { label: "Summarize" }
                        ],
                        centerText: "CIRCLES"
                    },
                    staticContent: [
                        "**CIRCLES Method:** A framework for product design interviews.",
                        "**5 Whys:** Ask 'Why' 5 times to get to the root cause.",
                        "**First Principles:** Boil things down to fundamental truths and reason up from there.",
                        "**Segmentation:** Don't build for 'everyone'. Build for 'Urban Millennials who own dogs'."
                    ]
                },
                {
                    title: "Competitive Analysis",
                    category: "Strategy",
                    desc: "Understanding the landscape, threats, and opportunities.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Market Share",
                        y: "Market Growth",
                        quadrants: [
                            { label: "Star", color: "#a855f7", desc: "High Share / High Growth" },
                            { label: "Cash Cow", color: "#22c55e", desc: "High Share / Low Growth" },
                            { label: "Dog", color: "#94a3b8", desc: "Low Share / Low Growth" },
                            { label: "Question Mark", color: "#eab308", desc: "Low Share / High Growth" }
                        ]
                    },
                    staticContent: [
                        "**SWOT:** Strengths, Weaknesses, Opportunities, Threats.",
                        "**Porter's 5 Forces:** Rivalry, Supplier Power, Buyer Power, Threat of Entry, Threat of Substitution.",
                        "**Feature Parity:** Sometimes you need to copy to compete (table stakes), but innovation wins.",
                        "**Moat:** Defensive advantage (Network effect, Brand, Switching cost, Data)."
                    ]
                },
                {
                    title: "OKRs & Goal Setting",
                    category: "Strategy",
                    desc: "Setting ambitious goals and tracking measurable results.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Initiatives (Projects)", color: "#0ea5e9" },
                            { label: "Key Results (Metrics)", color: "#6366f1" },
                            { label: "Objective (The Goal)", color: "#a855f7" }
                        ]
                    },
                    staticContent: [
                        "**Objective:** Qualitative, inspirational, time-bound. 'Become the #1 Music App'.",
                        "**Key Result:** Quantitative, measurable. 'Increase MAU from 10k to 50k'.",
                        "**Lagging Indicator:** Tells you what happened (Revenue). Hard to influence directly.",
                        "**Leading Indicator:** Tells you what might happen (Active Invite Invites sent). Easier to influence."
                    ]
                },
                {
                    title: "Influence Without Authority",
                    category: "Leadership",
                    desc: "Leading teams when you are not their boss.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Influence",
                        pillars: [
                            { label: "Trust", desc: "Relationship" },
                            { label: "Competence", desc: "Expertise" },
                            { label: "Empathy", desc: "Understanding" }
                        ],
                        foundation: "Communication"
                    },
                    staticContent: [
                        "**Social Proof:** People follow the crowd. 'Design already approved this'.",
                        "**Reciprocity:** Do favors for others, and they will help you.",
                        "**Bring Data:** Opinions are cheap. Data wins arguments.",
                        "**Inception:** Make them think it was their idea."
                    ]
                },
                {
                    title: "System Design for PMs",
                    category: "Technical",
                    desc: "Designing scalable systems at a high level without writing code.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "System Design",
                        pillars: [
                            { label: "Users", desc: "Who?" },
                            { label: "Interface", desc: "Mobile/Web" },
                            { label: "Data", desc: "Storage" },
                            { label: "Scale", desc: "Growth" }
                        ],
                        foundation: "CAPS (Constraints)"
                    },
                    staticContent: [
                        "**CAPS:** Constraints, APIs, Performance, Security.",
                        "**Monolith vs Microservices:** One big app vs many small services talking to each other.",
                        "**Synchronous vs Asynchronous:** Waiting for a response (Phone call) vs Fire and forget (Email).",
                        "**Scalability:** Vertical (Bigger machine) vs Horizontal (More machines)."
                    ]
                },
                {
                    title: "Product-Led Growth (PLG)",
                    category: "Growth",
                    desc: "Using the product itself as the primary engine for customer acquisition, retention, and expansion.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Acquire" },
                            { label: "Activate" },
                            { label: "Engage" },
                            { label: "Expand" }
                        ],
                        centerText: "Product"
                    },
                    staticContent: [
                        "**Freemium/Free Trial:** Key PLG strategies to get users experiencing value quickly.",
                        "**Self-Serve:** Empowering users to discover, adopt, and troubleshoot without human intervention.",
                        "**Virality:** Built-in mechanisms that encourage users to invite others (e.g., collaboration features).",
                        "**Value-First:** Focus on delivering immediate value to users, rather than selling features."
                    ]
                },
                {
                    title: "Experimentation & A/B Testing",
                    category: "Analytics",
                    desc: "Hypothesis-driven development to validate features and optimize product metrics.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Hypothesis" },
                            { label: "Design Experiment" },
                            { label: "Run Test" },
                            { label: "Analyze Results" },
                            { label: "Implement/Iterate" }
                        ],
                        centerText: "Learn"
                    },
                    staticContent: [
                        "**Hypothesis:** A testable statement (e.g., 'Changing button color to green will increase clicks by 5%').",
                        "**Statistical Significance:** Ensuring results are not due to random chance (p-value < 0.05).",
                        "**Sample Size:** Enough users to detect a meaningful difference.",
                        "**Guardrail Metrics:** Monitoring other key metrics to ensure the experiment doesn't negatively impact them."
                    ]
                }
            ]
        },
        tpm: {
            title: "Technical Program Management",
            subtitle: "Orchestrating Complex Systems and Scaling Engineering Excellence",
            categories: ["All", "Tech Fundamentals", "Lifecycle", "Technical", "Operations", "Risk", "Governance"],
            topics: [
                {
                    title: "Database Systems (SQL vs NoSQL)",
                    category: "Tech Fundamentals",
                    desc: "Understanding ACID properties, CAP theorem, sharding, replication, and when to use Relational vs. Document stores.",
                    studyTips: "Know the trade-offs: SQL for complex queries/transactions, NoSQL for scale/flexibility.",
                    image: "/images/tech-diagrams/db_architecture.png",
                    staticContent: [
                        "**ACID:** Atomicity (all or nothing), Consistency (valid state), Isolation (concurrent transactions), Durability (saved permanently). Essential for banking systems.",
                        "**CAP Theorem:** You can only have 2 of 3: Consistency, Availability, Partition Tolerance. In distributed systems, P is mandatory, so you choose between CP (strong consistency) or AP (high availability).",
                        "**SQL (Relational):** Best for structured data, complex joins, and transactions (PostgreSQL, MySQL). Vertically scalable.",
                        "**NoSQL (Non-Relational):** Best for unstructured data, high write throughput, and flexible schemas (MongoDB, Cassandra). Horizontally scalable via sharding."
                    ]
                },
                {
                    title: "API Design (REST & GraphQL)",
                    category: "Tech Fundamentals",
                    desc: "Designing robust interfaces with proper methods (GET/POST), status codes, rate limiting, and idempotency.",
                    studyTips: "Be ready to design an API for a vending machine or a parking lot.",
                    image: "/images/tech-diagrams/api_design.png",
                    staticContent: [
                        "**REST:** Resource-based (nouns). Uses standard HTTP methods (GET, POST, PUT, DELETE). Stateless and cacheable.",
                        "**GraphQL:** Client asks for exactly what it needs. Prevents over-fetching/under-fetching. Single endpoint.",
                        "**Status Codes:** 2xx (Success), 3xx (Redirect), 4xx (Client Error - 400 Bad Request, 401 Unauthorized), 5xx (Server Error - 500 Internal Error).",
                        "**Idempotency:** Making the same request multiple times has the same effect as making it once (essential for payments)."
                    ]
                },
                {
                    title: "System Performance Metrics",
                    category: "Tech Fundamentals",
                    desc: "Measuring health via Latency, Throughput, Bandwidth, and Availability (99.9% vs 99.999%).",
                    studyTips: "Latency is time to one result; Throughput is results per second.",
                    image: "/images/tech-diagrams/performance_metrics.png",
                    staticContent: [
                        "**Latency:** Time taken for a single request to travel from client to server and back (measured in ms). 'Fast' UI is <100ms.",
                        "**Throughput:** Number of requests a system can handle per second (RPS or QPS).",
                        "**Bandwidth:** Maximum data transfer rate of a network (bits/second).",
                        "**Availability:** Uptime. 99.9% (Three 9s) = 8.7 hours downtime/year. 99.999% (Five 9s) = 5 mins downtime/year."
                    ]
                },
                {
                    title: "Percentiles (P50, P99)",
                    category: "Tech Fundamentals",
                    desc: "Why averages lie: Understanding tail latency and ensuring performance for the worst-case user experience.",
                    studyTips: "P99 means 99% of requests are faster than this value. It defines the 'slow' user experience.",
                    image: "/images/tech-diagrams/percentiles.png",
                    staticContent: [
                        "**Why Averages Fail:** An average of 200ms could mean everyone gets 200ms, OR half get 10ms and half get 400ms. It hides outliers.",
                        "**P50 (Median):** 50% of users experience this speed or better.",
                        "**P99 (Tail Latency):** The experience of the slowest 1% of users. In a system with 1M users, P99 affects 10,000 unhappy people.",
                        "**SLA Definition:** SLAs are often written against P99 (e.g., 'P99 latency must be under 500ms')."
                    ]
                },
                {
                    title: "How the Internet Works",
                    category: "Tech Fundamentals",
                    desc: "The journey of a request: DNS resolution, TCP/IP handshake, SSL/TLS, HTTP/HTTPS, and CDN delivery.",
                    studyTips: "Can you explain what happens when you type 'google.com' into a browser?",
                    hasAnimation: true,
                    // image field removed/ignored in favor of animation, or kept as fallback
                    image: "/images/tech-diagrams/internet_flow.png",
                    staticContent: [
                        "**DNS (Phonebook):** Translates 'google.com' to an IP address (e.g., 142.250.190.46).",
                        "**TCP/IP Handshake:** SYN, SYN-ACK, ACK. Established a reliable connection between client and server.",
                        "**SSL/TLS:** Encryption handshake to secure the data (HTTPS).",
                        "**HTTP Request:** Browser sends a GET request. Server processes it and sends back HTML/JSON.",
                        "**CDN:** Static assets (images, CSS) are served from a server geographically closer to the user to reduce latency."
                    ]
                },
                {
                    title: "Caching Strategies",
                    category: "Tech Fundamentals",
                    desc: "Improving read performance using Write-through vs Write-back, LRU eviction, and Redis/Memcached.",
                    studyTips: "Caches are great but introduce complexity: Cache Invalidation is one of the hardest problems in CS.",
                    image: "/images/tech-diagrams/caching.png",
                    staticContent: [
                        "**Cache-Aside (Lazy Loading):** App checks cache. If miss, reads DB, updates cache, returns data. Good for heavy reads.",
                        "**Write-Through:** App writes to Cache and DB simultaneously. Data is always consistent but write latency is higher.",
                        "**Eviction (LRU):** Least Recently Used. When cache is full, delete the item that hasn't been accessed in the longest time.",
                        "**Redis vs Memcached:** Redis supports complex data structures (lists, sets) and persistence; Memcached is simple string/object key-value."
                    ]
                },
                {
                    title: "Load Balancing",
                    category: "Tech Fundamentals",
                    desc: "Distributing traffic across servers using algorithms like Round Robin or Least Connections (L4 vs L7).",
                    studyTips: "Load balancers also provide health checks and SSL termination.",
                    image: "/images/tech-diagrams/load_balancing.png",
                    staticContent: [
                        "**Purpose:** Prevents any single server from being overwhelmed. Increases availability.",
                        "**Round Robin:** Distributes requests sequentially (A, B, C, A, B...). Simple but assumes all requests are equal.",
                        "**Least Connections:** Sends traffic to the server with fewest active connections. Better for long-running tasks.",
                        "**L4 vs L7:** L4 (Transport) makes decisions based on IP/Port. L7 (Application) can route based on URL path or Cookies (e.g., /api goes to Server A, /images goes to Server B)."
                    ]
                },
                {
                    title: "Consistency Models",
                    category: "Tech Fundamentals",
                    desc: "Strong vs. Eventual Consistency. Understanding data synchronization in distributed systems.",
                    studyTips: "Eventual consistency allows for high availability but potential temporary stale data.",
                    image: "/images/tech-diagrams/consistency.png",
                    staticContent: [
                        "**Strong Consistency:** Every read receives the most recent write. Users always see the same data. Higher latency (must wait for replication).",
                        "**Eventual Consistency:** Reads *eventually* reflect the latest write. Fast and highly available, but users might see old data briefly (e.g., number of likes on a viral post).",
                        "**Causal Consistency:** Events that are casually related are seen by everyone in the same order.",
                        "**Use Case:** Bank balance needs Strong Consistency. Social media feed can use Eventual Consistency."
                    ]
                },
                {
                    title: "Microservices vs Monolith",
                    category: "Tech Fundamentals",
                    desc: "Architectural styles: Single deployable unit vs loosely coupled services.",
                    studyTips: "Microservices scale teams but add operational complexity (latency, debuggability).",
                    image: "/images/tech-diagrams/microservices.png",
                    staticContent: [
                        "**Monolith:** Simpler to start, easy to test/deploy initially. Becomes 'spaghetti code' as it scales. Single Point of Failure.",
                        "**Microservices:** Independent deployment, technology diversity (Java for one service, Python for another), fault isolation.",
                        "**Complexity:** Requires heavy DevOps, service discovery, distributed tracing, and handling partial failures.",
                        "**When to Switch:** When the team size is too large to coordinate on one codebase, not just because it's trendy."
                    ]
                },
                {
                    title: "Program Lifecycle (Hybrid)",
                    category: "Lifecycle",
                    desc: "End-to-end program management from Initiative to Close, featuring Agile execution loops.",
                    studyTips: "Most modern programs are hybrid: Linear planning (Waterfall) + Iterative execution (Agile).",
                    hasLifecycleAnimation: true
                },
                {
                    title: "Global Program Orchestration",
                    category: "Lifecycle",
                    desc: "Driving large-scale technical programs from concept to launch across international regions.",
                    example: "Spearheaded the Global Merchant Bill Payment Portal expansion beyond Mexico to a global scale.",
                    image: "/images/tech-diagrams/global_orchestration.png",
                    staticContent: [
                        "**Localization Strategy:** Adapting product for local languages, currencies, and regulations (GDPR, local tax laws).",
                        "**Rolling Launches:** Phased rollout (e.g., 5% in UK -> 100% in UK -> Launch in France) to manage risk.",
                        "**Async Communication:** Managing teams across timezones (PST, EST, IST) using written docs over meetings.",
                        "**Hub & Spoke Model:** Central HQ sets strategy; Regional Hubs execute with local nuance."
                    ]
                },
                {
                    title: "Large-Scale Financial Systems",
                    category: "Technical",
                    desc: "Managing high-stakes financial platform migrations and integrations.",
                    example: "Managed the $25M+ expansion of Plan N across Latin America and the Caribbean.",
                    image: "/images/tech-diagrams/financial_ledger.png",
                    staticContent: [
                        "**Double-Entry Ledger:** Every transaction has a Debit and a Credit. Assets = Liabilities + Equity. Zero-sum.",
                        "**Idempotency:** Crucial for payments. Charging $10 twice is a disaster. Use unique keys (UUIDs) for every request.",
                        "**Reconciliation:** Automated daily jobs to compare Internal Ledger vs Bank Statements.",
                        "**Compliance:** PCI-DSS (Card security), SOX (Financial reporting), AML (Anti-Money Laundering)."
                    ]
                },
                {
                    title: "System Design for TPMs",
                    category: "Technical",
                    desc: "Facilitating architectural discussions and scaling trade-off analysis.",
                    example: "Led development of large-scale software using Java, React, and cloud technologies with focus on performance tuning.",
                    image: "/images/tech-diagrams/system_design.png",
                    staticContent: [
                        "**Scalability:** Vertical (bigger machine) vs Horizontal (more machines).",
                        "**Reliability:** System continues to work despite failures (Redundancy, Retries).",
                        "**Maintainability:** Operability (monitoring), Simplicity (abstraction), Evolvability.",
                        "**SPOF:** Single Point of Failure. Identify it and add redundancy.",
                        "**Back of Envelope:** Estimating storage/bandwidth. (e.g., 1M users, 10KB each = 10GB storage)."
                    ]
                },
                {
                    title: "Dependency & Path Visualization",
                    category: "Governance",
                    desc: "Mapping complex inter-team dependencies to identify the critical path.",
                    studyTips: "Use Gantt charts or Dependency graphs to spot bottlenecks early."
                },
                {
                    title: "Operational Excellence (SRE)",
                    category: "Operations",
                    desc: "Establishing SLIs/SLOs, monitoring, and incident response protocols.",
                    example: "Ensured seamless handling of trillions of dollars in transactions with substantial cost savings.",
                    staticContent: [
                        "**SLI (Indicator):** What you measure (e.g., latency, error rate).",
                        "**SLO (Objective):** The goal (e.g., 99.9% of requests < 200ms). The target you alert on.",
                        "**SLA (Agreement):** Contract with the customer. Breach = Penalty.",
                        "**Error Budget:** 100% - SLO. If you burn your budget, no more releases until stability returns.",
                        "**MTTR:** Mean Time To Recovery. The most important metric for incident response."
                    ]
                },
                {
                    title: "Cloud Infrastructure (AWS)",
                    category: "Technical",
                    desc: "Navigating cloud services for scalable and cost-effective solutions.",
                    example: "Utilized Cloud Services (DynamoDB, Lambda, Amplify) to deliver high-impact software programs.",
                    image: "/images/tech-diagrams/cloud.png",
                    staticContent: [
                        "**IaaS (Infrastructure):** You manage OS, runtime. AWS EC2, Google Compute Engine. Max control.",
                        "**PaaS (Platform):** You manage code, provider manages OS/Runtime. Heroku, AWS Elastic Beanstalk. Faster dev.",
                        "**SaaS (Software):** You use the app. Gmail, Salesforce. Zero management.",
                        "**Shared Responsibility:** Provider secures the 'Cloud' (hardware), you secure what's 'IN the Cloud' (data, access)."
                    ]
                },
                {
                    title: "API Design & Contracts",
                    category: "Technical",
                    desc: "Ensuring stable contracts between microservices and external partners.",
                    studyTips: "Prioritize backward compatibility to avoid breaking downstream consumers."
                },
                {
                    title: "Release Management & CI/CD",
                    category: "Lifecycle",
                    desc: "Coordinating multi-tier deployments and blue/green release strategies.",
                    studyTips: "Automate as much as possible to reduce human error during hotfixes.",
                    image: "/images/tech-diagrams/cicd.png",
                    staticContent: [
                        "**CI (Continuous Integration):** Developers merge code frequently. Automated tests run on every commit.",
                        "**CD (Continuous Deployment):** Code is automatically deployed to production if tests pass.",
                        "**Blue/Green Deployment:** Two identical environments. Blue is live. Deploy to Green. Test. Switch traffic. Instant rollback.",
                        "**Canary Release:** Deploy to a small % of users first (e.g., 5%). Monitor errors. Gradually roll out to 100%."
                    ]
                },
                {
                    title: "Vendor & Partner Management",
                    category: "Operations",
                    desc: "Coordinating 3rd party integrations and external service level agreements.",
                    example: "Managed technical projects and products, overseeing delivery across vendors."
                },
                {
                    title: "Scalability & Fault Tolerance",
                    category: "Technical",
                    desc: "Designing systems that handle 10x growth and recover gracefully from failure.",
                    studyTips: "Understand horizontal scaling and the 'Circuit Breaker' pattern.",
                    staticContent: [
                        "**Circuit Breaker:** Fails fast to prevent cascading failures. Open -> Half-Open -> Closed.",
                        "**Bulkhead Pattern:** Isolating components (like ship compartments) so one failure doesn't sink the whole system.",
                        "**Throttling:** Limiting requests to protect downstream services.",
                        "**Graceful Degradation:** Showing cached/stale data instead of a 500 Error when a service is down."
                    ]
                },
                {
                    title: "Security & Compliance (SOC2)",
                    category: "Risk",
                    desc: "Ensuring data privacy and security best practices are integrated into the SDLC.",
                    studyTips: "Shift security 'Left' by integrating audits early in development.",
                    staticContent: [
                        "**CIA Triad:** Confidentiality, Integrity, Availability.",
                        "**Authentication vs Authorization:** AuthN (Who are you? - OIDC) vs AuthZ (What can you do? - RBAC).",
                        "**Encryption:** At Rest (AES-256) and In Transit (TLS 1.3).",
                        "**GDPR/CCPA:** Right to be forgotten. PII (Personally Identifiable Information) handling."
                    ]
                },
                {
                    title: "Agile at Scale (SAFe)",
                    category: "Governance",
                    desc: "Implementing frameworks that allow 10+ teams to move in sync.",
                    studyTips: "Standardize process where needed, but allow team-level flexibility.",
                    staticContent: [
                        "**SAFe vs LeSS:** Scaled Agile Framework (Structured, heavy) vs Large-Scale Scrum (Lightweight).",
                        "**Release Train:** Aligned cadence for multiple teams to release simultaneously.",
                        "**Scrum of Scrums:** Meta-standup for representatives from each team to resolve blockers.",
                        "**PI Planning:** Program Increment planning every 8-12 weeks."
                    ]
                },
                {
                    title: "Post-Mortem Culture",
                    category: "Operations",
                    desc: "Driving root-cause analysis that focuses on system failure, not human error.",
                    studyTips: "Transparency and trust are key to preventing the same bug twice.",
                    staticContent: [
                        "**Blameless:** Focus on process/tooling failures, not 'Who pressed the button?'.",
                        "**5 Whys:** Iterative interrogation technique to find the root cause.",
                        "**COE (Correction of Error):** The formal document capturing Impact, Root Cause, and Action Items.",
                        "**Action Items:** Must be SMART (Specific, Measurable, Assignable, Realistic, Time-related)."
                    ]
                },
                {
                    title: "Program Metrics & KPIs",
                    category: "Governance",
                    desc: "Measuring what matters: Velocity, Quality, Throughput, and Lead Time.",
                    studyTips: "Data should tell a story that leads to actionable program adjustments.",
                    staticContent: [
                        "**DORA Metrics:** Deployment Frequency, Lead Time for Changes, Time to Restore Service, Change Failure Rate.",
                        "**Velocity is Internal:** Never compare velocity between teams. It's a capacity planning tool, not a performance metric.",
                        "**Say-Do Ratio:** Did the team deliver what they committed to? (Target > 80%).",
                        "**Burn-down Charts:** Visualizing work remaining vs time."
                    ]
                },
                {
                    title: "Resource Planning & Budgeting",
                    category: "Governance",
                    desc: "Strategic allocation of engineering talent and infrastructure spend.",
                    studyTips: "Balance 'Keep-The-Lights-On' (KTLO) with new feature development.",
                    staticContent: [
                        "**OPEX vs CAPEX:** Operating Expense (Cloud bills, Salaries) vs Capital Expense (Servers, Laptops).",
                        "**Headcount Planning:** Forecasting hiring needs based on roadmap.",
                        "**KTLO:** 'Keep The Lights On'. Maintenance work. Should be <30% of capacity.",
                        "**T-Shirt Sizing:** Estimating effort (S, M, L, XL) for high-level roadmap planning."
                    ]
                },
                {
                    title: "Chaos Engineering",
                    category: "Operations",
                    desc: "Testing system resilience by intentionally introducing controlled failures.",
                    studyTips: "Failure is inevitable; start with small-radius experiments.",
                    staticContent: [
                        "**Simian Army:** Netflix's suite (Chaos Monkey kills instances, Chaos Kong kills regions).",
                        "**Game Days:** Scheduled exercises to practice incident response.",
                        "**Hypothesis:** 'If DB replica fails, master should take over in < 5s'.",
                        "**Blast Radius:** Start small (staging/canary) before unleashing chaos on prod."
                    ]
                },
                {
                    title: "API-First Strategy",
                    category: "Technical",
                    desc: "Designing interfaces before implementation to allow parallel development.",
                    studyTips: "Enforce API standards Org-wide for developer consistency.",
                    staticContent: [
                        "**OpenAPI (Swagger):** The standard for describing REST APIs. Contract-first.",
                        "**Mocking:** FE teams can build UI against mocks while BE teams build the implementation.",
                        "**Consistency:** Standardizing error objects, timestamp formats (ISO 8601), and pagination.",
                        "**Governance:** Using linters/gatekeepers to ensure new APIs follow the spec."
                    ]
                },
                {
                    title: "Technical Debt Governance",
                    category: "Governance",
                    desc: "Tracking and prioritizing the removal of systemic technical debt.",
                    studyTips: "Quantify the cost of delay for technical debt to justify prioritization.",
                    staticContent: [
                        "**Tech Debt Quadrant:** Deliberate/Prudent (Shipping MVP) vs Inadvertent/Reckless (Bad coding).",
                        "**Refactoring:** Improving code structure without changing external behavior.",
                        "**Deprecation:** formally retiring old services to reduce maintenance burden.",
                        "**20% Rule:** Allocating 20% of sprint capacity to debt/maintenance."
                    ]
                },
                {
                    title: "Distributed Systems",
                    category: "Technical",
                    desc: "Orchestrating service communication, discovery, and observability.",
                    studyTips: "Understand the trade-offs between Monoliths and Microservices.",
                    staticContent: [
                        "**Service Discovery:** How Microservice A finds Microservice B (Consul, Eureka).",
                        "**Event-Driven:** Async communication via Message Queues (Kafka, SQS) decouples services.",
                        "**Pub/Sub:** Publisher sends message, Subscriber listens. Many-to-many.",
                        "**Distributed Tracing:** Following a request across 10+ services (Jaeger, Zipkin)."
                    ]
                },
                {
                    title: "Developer Experience (DX)",
                    category: "Operations",
                    desc: "Standardizing the developer experience to decrease time-to-first-commit.",
                    studyTips: "Treat internal engineers as your 'customers'.",
                    staticContent: [
                        "**IDP (Internal Developer Platform):** Self-service portal to spin up infra/environments (Backstage).",
                        "**Golden Paths:** Paved roads/Templates for new services that come with best practices built-in.",
                        "**CI/CD Friction:** Measuring how long a build takes. Waiting 1hr for a build kills productivity.",
                        "**Documentation:** 'Docs as Code'. Living documentation close to the source."
                    ]
                },
                {
                    title: "Data Engineering & Analytics",
                    category: "Technical",
                    desc: "Pipelines, Warehouses, and Lakes. Moving data from Source to Insight.",
                    studyTips: "Crucial for 'Data TPM' roles. Understand the flow of data.",
                    staticContent: [
                        "**ETL vs ELT:** Extract-Transform-Load (Traditional) vs Extract-Load-Transform (Modern/Snowflake).",
                        "**Data Warehouse:** Structured data for BI/Reporting (Snowflake, Redshift). Truth.",
                        "**Data Lake:** Unstructured/Raw data (S3, Hadoop). Exploration.",
                        "**Stream Processing:** Real-time data (Kafka/Flink) vs Batch Processing (Nightly jobs)."
                    ]
                },
                {
                    title: "Cloud Native & Kubernetes",
                    category: "Technical",
                    desc: "Modern infrastructure primitives: Containers, Orchestration, and Service Mesh.",
                    studyTips: "K8s is the OS of the cloud.",
                    staticContent: [
                        "**Containers (Docker):** Packaging code + dependencies. Runs anywhere.",
                        "**Kubernetes (K8s):** Orchestrating containers. Managing 'Pods' (groups of containers) across 'Nodes' (servers).",
                        "**Service Mesh (Istio):** Dedicated infrastructure layer for service-to-service communication details (TLS, Retry).",
                        "**Significance:** Enables true portability and massive scaling."
                    ]
                },
                {
                    title: "TPM System Design (The Interview)",
                    category: "Technical",
                    desc: "How to approach the System Design interview as a TPM vs an Engineer.",
                    studyTips: "Focus on Requirements, Constraints, and Trade-offs, not just drawing boxes.",
                    staticContent: [
                        "**Step 1: Clarify Requirements:** Functional (What it does) & Non-Functional (Scale, Latency).",
                        "**Step 2: Estimation:** Back-of-envelope math. Do we need 1 server or 1000?",
                        "**Step 3: High-Level Design:** Draw the big boxes (LB, App, DB).",
                        "**TPM Focus:** Don't get stuck on coding. Focus on 'What happens if this breaks?' and 'How do we rollout?'"
                    ]
                },
                {
                    title: "Event Streaming & Kafka",
                    category: "Technical",
                    desc: "Decoupling microservices via high-throughput, fault-tolerant message buses.",
                    studyTips: "Kafka isn't just a queue; it's a log. Consumers track their own offsets.",
                    staticContent: [
                        "**Topics & Partitions:** Data is stored in 'Topics' (Categories) which are split into 'Partitions' for parallel processing.",
                        "**Producers & Consumers:** Producers write events. Consumers read them. Decoupled.",
                        "**Consumer Groups:** Multiple instances of a service reading from the same topic to share the load.",
                        "**Retention:** Messages stay for a set time (e.g., 7 days) even after being read (unlike a Queue which deletes).",
                        "**Use Case:** Real-time analytics (Clickstreams), Event Sourcing, Log Aggregation."
                    ]
                }
            ]
        },
        em: {
            title: "Engineering Management",
            subtitle: "Cultivating High-Performance Cultures and Engineering Excellence",
            categories: ["All", "Leadership", "Strategy", "Culture", "Growth", "Execution"],
            topics: [
                {
                    title: "Coaching & Effective 1:1s",
                    category: "Growth",
                    desc: "Building high-trust teams through regular, actionable feedback and mentorship.",
                    example: "Mentored diverse talent pools, ensuring a culture of innovation and engineering excellence.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Connect (Personal)" },
                            { label: "Discuss (Blockers)" },
                            { label: "Feedback (2-Way)" },
                            { label: "Action (Growth)" }
                        ],
                        centerText: "1:1"
                    },
                    staticContent: [
                        "**The GROW Model:** Goal (What do you want?), Reality (Where are we now?), Options (What could you do?), Will (What will you do?).",
                        "**Active Listening:** Listen to understand, not to respond. Reflect back what you heard.",
                        "**Frequency:** Weekly or Bi-weekly. Never cancel, only reschedule. It shows you value them.",
                        "**Not a Status Update:** Use the time for career growth, wellbeing, and unblocking - not just Jira updates."
                    ]
                },
                {
                    title: "Performance Management",
                    category: "Growth",
                    desc: "Setting clear expectations, handling underperformance, and rewarding top talent.",
                    studyTips: "No performance review should ever be a surprise.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "WILL / Motivation",
                        y: "SKILL / Capability",
                        quadrants: [
                            { label: "Excite / Motivate", color: "#3b82f6", desc: "High Skill / Low Will" },
                            { label: "Delegate / Promote", color: "#22c55e", desc: "High Skill / High Will" },
                            { label: "Direct / PIP", color: "#ef4444", desc: "Low Skill / Low Will" },
                            { label: "Guide / Train", color: "#eab308", desc: "Low Skill / High Will" }
                        ]
                    },
                    staticContent: [
                        "**Radical Candor:** Care Personally + Challenge Directly. Silence is not kind.",
                        "**PIP (Performance Improvement Plan):** A formal document. Should be a last resort, but executed fairly.",
                        "**High Performers:** Don't ignore them. Give them harder problems to solve (Retention).",
                        "**SBI Model:** Situation, Behavior, Impact. 'In the meeting (S), you interrupted X (B), which shut down their idea (I).'"
                    ]
                },
                {
                    title: "Psychological Safety",
                    category: "Culture",
                    desc: "Creating an environment where engineers feel safe to take risks and fail.",
                    example: "Fostered empathy and motivation to care about project success across teams.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Challenger Safety (Innovate)", color: "#6366f1" },
                            { label: "Contributor Safety (Participate)", color: "#818cf8" },
                            { label: "Learner Safety (Ask Questions)", color: "#a5b4fc" },
                            { label: "Inclusion Safety (Belonging)", color: "#c7d2fe" }
                        ]
                    },
                    staticContent: [
                        "**Defined:** The belief that you won't be punished or humiliated for speaking up with ideas, questions, concerns, or mistakes.",
                        "**Google's Project Aristotle:** Found Psychological Safety was the #1 predictor of high-performing teams.",
                        "**Blameless Post-Mortems:** Focus on the process/system failure, not the person.",
                        "**Leader Modeling:** Admit your own mistakes. 'I don't know' is a powerful phrase."
                    ]
                },
                {
                    title: "Hiring & Talent Brand",
                    category: "Growth",
                    desc: "Defining interview loops, evaluating culture fit, and scaling the engineering brand.",
                    example: "Collaborated with cross-functional teams to deliver project requirements and built strong teams.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Sourcing & Brand" },
                            { label: "Interview Loop" },
                            { label: "Debrief/Bar Raiser" },
                            { label: "Closing & Onboard" }
                        ]
                    },
                    staticContent: [
                        "**Bar Raiser:** An interviewer from outside the hiring team with veto power to ensure quality doesn't drop.",
                        "**Structured Interviewing:** Asking the same questions to all candidates to reduce bias.",
                        "**Signal vs Noise:** Focus on data points (code, design), not personality quirks.",
                        "**ABR:** Always Be Recruiting. Your network is your net worth."
                    ]
                },
                {
                    title: "Technical Strategy Alignment",
                    category: "Strategy",
                    desc: "Ensuring engineering efforts directly drive business objectives.",
                    example: "Led technical strategy for tax automation initiatives, resulting in significant business impact.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Business Value",
                        pillars: [
                            { label: "Tech Strategy", desc: "The 'How'" },
                            { label: "Product Roadmap", desc: "The 'What'" },
                            { label: "Company Vision", desc: "The 'Why'" }
                        ],
                        foundation: "Engineering Execution"
                    },
                    staticContent: [
                        "**The Gap:** Engineers often want to refactor; Business wants features. Your job is to translate.",
                        "**ROI of Tech:** Explain refactoring in terms of 'Velocity' or 'Risk Reduction' ($$$).",
                        "**OKRs:** Objectives and Key Results. Align individual goals to the company's north star.",
                        "**Buy vs Build:** Don't build commodity software (e.g., Auth, Billing) unless it's your core differentiator."
                    ]
                },
                {
                    title: "Conflict Resolution",
                    category: "Leadership",
                    desc: "Navigating deep technical impasses between star engineers objectively.",
                    studyTips: "Focus on facts and business impact rather than personal opinions.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Listen (Separate sides)" },
                            { label: "Identify Shared Goal" },
                            { label: "Explore Options" },
                            { label: "Commit to Decision" }
                        ],
                        centerText: "Resolve"
                    },
                    staticContent: [
                        "**Disagree and Commit:** Teams can argue passionately, but once a decision is made, everyone supports it fully.",
                        "**Positions vs Interests:** Position: 'I want React'. Interest: 'I want fast dev cycles'. Solve for interests.",
                        "**Escalation:** It's okay to escalate if you can't resolve, but bring potential solutions, not just problems.",
                        "**Facts over Feelings:** 'The data shows X' beats 'I feel like X'."
                    ]
                },
                {
                    title: "Change Management",
                    category: "Leadership",
                    desc: "Leading teams through pivots, re-orgs, and shifts in technology stack.",
                    studyTips: "Communicate the 'Why' early and often to maintain team morale.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Awareness (Why?)" },
                            { label: "Desire (WIIFM)" },
                            { label: "Knowledge (How?)" },
                            { label: "Ability (Skills)" },
                            { label: "Reinforcement (Stick)" }
                        ],
                        centerText: "ADKAR"
                    },
                    staticContent: [
                        "**ADKAR Model:** Awareness, Desire, Knowledge, Ability, Reinforcement.",
                        "**The J-Curve:** Performance often drops before it improves after a change.",
                        "**Over-communication:** When you are tired of saying it, they are just starting to hear it.",
                        "**Empathy:** specific changes (like re-orgs) are traumatic. Validate their feelings."
                    ]
                },
                {
                    title: "Engineering Excellence Pillars",
                    category: "Strategy",
                    desc: "Defining what 'Good' looks like for Code Quality, Testing, and Reliability.",
                    example: "Ensured high-precision products verified to the highest quality standards.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Operational Excellence",
                        pillars: [
                            { label: "Code Quality", desc: "Readable, Maintainable" },
                            { label: "Testing", desc: "Unit, Integ, E2E" },
                            { label: "Security", desc: "Shift Left" },
                            { label: "Observability", desc: "Logs, Metrics" }
                        ],
                        foundation: "Developer Culture"
                    },
                    staticContent: [
                        "**Code Reviews:** Not just for catching bugs, but for knowledge sharing and maintaining standards.",
                        "**CI/CD:** Automated pipelines. If it's not automated, it's not 'Excellent'.",
                        "**Documentation:** READMEs, API specs, and Architecture Decision Records (ADRs).",
                        "**Tech Radar:** Defining which technologies are 'Adopt', 'Trial', 'Assess', or 'Hold'."
                    ]
                },
                {
                    title: "Career Ladders & Growth",
                    category: "Growth",
                    desc: "Defining clear, fair expectations for growth from Junior to Principal level.",
                    studyTips: "A ladder should be a roadmap for growth, not just a rubric for assessment.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Principal (Org-wide Impact)", color: "#a855f7" },
                            { label: "Staff (Multi-team Impact)", color: "#8b5cf6" },
                            { label: "Senior (Team Owner)", color: "#6366f1" },
                            { label: "Mid-Level (Independent)", color: "#3b82f6" },
                            { label: "Junior (Task Execution)", color: "#0ea5e9" }
                        ]
                    },
                    staticContent: [
                        "**Scope of Influence:** Junior (Task) -> Senior (Feature/Team) -> Staff (System/Org).",
                        "**Dual Track:** Individual Contributor (IC) vs Management. Forced management validation is bad.",
                        "**Promotion Packets:** Evidence-based document proving the candidate is already operating at the next level.",
                        "**Lagging Indicator:** Promotions recognize work already being done, they don't enable it."
                    ]
                },
                {
                    title: "Diversity & Inclusion (D&I)",
                    category: "Culture",
                    desc: "Building representative teams and fostering an inclusive workspace.",
                    studyTips: "Inclusion is an active, daily practice, not a one-time initiative.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Inclusive Culture",
                        pillars: [
                            { label: "Hiring", desc: "Pipeline / Bias" },
                            { label: "Retention", desc: "Belonging" },
                            { label: "Progression", desc: "Fair Ladder" }
                        ],
                        foundation: "Leadership Commitment"
                    },
                    staticContent: [
                        "**Rooney Rule:** Mandating that at least one underrepresented candidate is interviewed for senior roles.",
                        "**Unconscious Bias:** We all have it. Training helps us recognize it in decision making.",
                        "**Allyship:** Using your privilege/position to support those with less.",
                        "**Diverse Teams Build Better Products:** They avoid echo chambers and blind spots."
                    ]
                },
                {
                    title: "Engineering Metrics (DORA)",
                    category: "Execution",
                    desc: "Monitoring Deployment Frequency, Lead Time, MTTR, and Change Failure Rate.",
                    studyTips: "Don't use productivity metrics to micromanage individuals.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Elite Performance",
                        pillars: [
                            { label: "Deploy Freq", desc: "How often?" },
                            { label: "Lead Time", desc: "Code to Prod" },
                            { label: "MTTR", desc: "Restore Svc" },
                            { label: "Fail Rate", desc: "% of Bugs" }
                        ],
                        foundation: "DevOps Practices"
                    },
                    staticContent: [
                        "**Speed Metrics:** Deployment Frequency + Lead Time for Changes.",
                        "**Stability Metrics:** Change Failure Rate + Time to Restore Service.",
                        "**Goodhart's Law:** When a measure becomes a target, it ceases to be a good measure.",
                        "**Flow Metrics:** Cycle time, Throughput, Work in Progress (WIP)."
                    ]
                },
                {
                    title: "Tech Debt vs. Features",
                    category: "Execution",
                    desc: "Advocating for 20-30% of sprint capacity to be dedicated to system health.",
                    studyTips: "Frame technical debt in terms of future speed and risk.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Intentionality",
                        y: "Visibility",
                        quadrants: [
                            { label: "Reckless", color: "#ef4444", desc: "No Design / 'Just ship it'" },
                            { label: "Prudent", color: "#eab308", desc: "MVP / 'We'll fix later'" },
                            { label: "Inadvertent", color: "#f97316", desc: "Learning / 'Now we know better'" },
                            { label: "Deliberate", color: "#22c55e", desc: "Planned / Refactoring" }
                        ]
                    },
                    staticContent: [
                        "**The Metaphor:** Financial debt. You pay 'interest' (slower dev speed) on the principal (bad code).",
                        "**Refactoring:** Cleaning the kitchen while you cook, not stopping cooking to clean.",
                        "**Boy Scout Rule:** Leave the code better than you found it.",
                        "**Negotiation:** Don't ask for permission to write good code. It's part of the job."
                    ]
                },
                {
                    title: "System Thinking",
                    category: "Strategy",
                    desc: "Understanding how changes in one part of the org impact distant teams.",
                    studyTips: "Look for bottlenecks at the 'System' level, not just the 'Team' level.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Input" },
                            { label: "Process" },
                            { label: "Output" },
                            { label: "Feedback Loop" }
                        ],
                        centerText: "System"
                    },
                    staticContent: [
                        "**Local Optima vs Global Optima:** Maximizing one team's speed might slow down the whole org (e.g., breaking APIs).",
                        "**Theory of Constraints:** Any system is limited by a single constraint (bottleneck). Optimize that.",
                        "**Second-Order Effects:** 'If we do X, then Y happens, which causes Z.' Think about Z.",
                        "**Conway's Law:** Systems resemble the communication structure of the organization."
                    ]
                },
                {
                    title: "Crisis Management",
                    category: "Leadership",
                    desc: "Staying calm during Tier-1 outages and leading the team through resolution.",
                    studyTips: "Practice 'Hot Swaps' of leadership roles during non-critical drills.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Detect & Alert" },
                            { label: "Triage & Contain" },
                            { label: "Resolve" },
                            { label: "Analyze (RCA)" }
                        ]
                    },
                    staticContent: [
                        "**Command & Control:** In a crisis, consensus is too slow. Designate an Incident Commander (IC).",
                        "**Communication:** Updates every 30 mins. Stakeholders need to know you are on it.",
                        "**Psychology:** Panic is contagious. Calm is also contagious. Be the anchor.",
                        "**Runbooks:** Don't rely on memory at 3 AM. Have step-by-step guides."
                    ]
                },
                {
                    title: "Resource Allocation",
                    category: "Execution",
                    desc: "Identifying when to hire, when to outsource, and when to optimize existing teams.",
                    studyTips: "Hiring more people isn't always the solution to late projects.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Transform (Innovation/New Bets)", color: "#a855f7" },
                            { label: "Grow (Features/Scale)", color: "#3b82f6" },
                            { label: "Run (KTLO/Maintenance)", color: "#64748b" }
                        ]
                    },
                    staticContent: [
                        "**Brooks' Law:** Adding manpower to a late software project makes it later.",
                        "**CapEx vs OpEx:** Capital Expenses (Assets) vs Operating Expenses (Salaries). Knowledge is key for budgeting.",
                        "**Elasticity:** Using contractors for short-term bursts vs FTEs for core domain knowledge.",
                        "**Opportunity Cost:** 'If we do A, we cannot do B'. capability to say 'No'."
                    ]
                },
                {
                    title: "Internal Tooling (DX)",
                    category: "Execution",
                    desc: "Investing in internal tools that multiply the productivity of the entire team.",
                    studyTips: "Good internal tools can be the differentiator for top-talent retention.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Identify Friction" },
                            { label: "Build MVP" },
                            { label: "Drive Adoption" },
                            { label: "Iterate" }
                        ],
                        centerText: "DX"
                    },
                    staticContent: [
                        "**Leverage:** A tool that saves 100 engineers 1 hour/week saves 5,000 hours/year.",
                        "**Treat as Product:** Internal tools need PMs, design, and SLAs too. Don't ship junk to your peers.",
                        "**Golden Path:** The supported, opinionated way to build services. Make the right way the easy way.",
                        "**Self-Service:** Developers shouldn't have to open a ticket to get a database."
                    ]
                },
                {
                    title: "High-Performing Squads",
                    category: "Culture",
                    desc: "The journey from Forming, Storming, Norming, to Performing.",
                    studyTips: "Invest heavily in the 'Norming' phase to build shared values.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Forming" },
                            { label: "Storming" },
                            { label: "Norming" },
                            { label: "Performing" }
                        ]
                    },
                    staticContent: [
                        "**Tuckman's Model:** All teams go through this cycle. 'Storming' is painful but necessary.",
                        "**Autonomous:** They have all the skills needed to ship (Front, Back, Design, QA).",
                        "**Mission Driven:** They solve a 'Problem', not just build a 'Feature'.",
                        "**Lencioni's 5 Dysfunctions:** Absence of Trust, Fear of Conflict, Lack of Commitment, Avoidance of Accountability, Inattention to Results."
                    ]
                },
                {
                    title: "Architecture Decisions (ADRs)",
                    category: "Strategy",
                    desc: "Standardizing how major technical decisions are documented and reviewed.",
                    studyTips: "ADRs provide historical context that prevents future 'Why did we do this?' questions.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Impact",
                        y: "Reversibility",
                        quadrants: [
                            { label: "Safe Bet", color: "#22c55e", desc: "Low Impact / Reversible" },
                            { label: "Quick Decision", color: "#3b82f6", desc: "High Impact / Reversible" },
                            { label: "Analyze", color: "#eab308", desc: "Low Impact / Irreversible" },
                            { label: "Architect Review", color: "#ef4444", desc: "High Impact / Irreversible" }
                        ]
                    },
                    staticContent: [
                        "**Context:** Why are we making this decision?",
                        "**Options:** What alternatives did we consider?",
                        "**Decision:** What did we pick?",
                        "**Consequences:** What are the trade-offs (positive and negative)?",
                        "**Type 1 vs Type 2 Decisions:** Irreversible (One-way door) vs Reversible (Two-way door). Spend time on Type 1."
                    ]
                },
                {
                    title: "Sponsorship",
                    category: "Growth",
                    desc: "Using social capital to open doors for underrepresented talent.",
                    studyTips: "Be a sponsor, not just a mentor.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Sponsor (Advocates Behind Closed Doors)", color: "#a855f7" },
                            { label: "Coach (Talks TO you)", color: "#8b5cf6" },
                            { label: "Mentor (Talks WITH you)", color: "#6366f1" }
                        ]
                    },
                    staticContent: [
                        "**Mentorship vs Sponsorship:** A mentor gives advice. A sponsor gives opportunity.",
                        "**Spending Capital:** Sponsorship costs you something (reputation/risk). It's an active investment.",
                        "**Exposure:** Putting high-potential reports in front of leadership.",
                        "**Advocacy:** Speaking up for someone when they are not in the room."
                    ]
                },
                {
                    title: "Emotional Intelligence (EQ)",
                    category: "Leadership",
                    desc: "Mastering self-awareness and empathy as the primary leadership tools.",
                    studyTips: "EQ is often more important than IQ in high-stress management roles.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Regulation",
                        y: "Awareness",
                        quadrants: [
                            { label: "Self-Management", color: "#3b82f6", desc: "Self + Action" },
                            { label: "Self-Awareness", color: "#6366f1", desc: "Self + Knowledge" },
                            { label: "Relationship Mgmt", color: "#22c55e", desc: "Social + Action" },
                            { label: "Social Awareness", color: "#eab308", desc: "Social + Knowledge" }
                        ]
                    },
                    staticContent: [
                        "**Self-Awareness:** Recognizing your own triggers and emotional state.",
                        "**Self-Management:** Controlling your impulses. Pausing before reacting.",
                        "**Social Awareness:** Empathy. Reading the room. Understanding organizational politics.",
                        "**Relationship Management:** Influence, conflict management, and teamwork."
                    ]
                }
            ]
        },
        swe: {
            title: "Software Engineering",
            subtitle: "Algorithms, Systems, and Craftsmanship",
            categories: ["All", "Java Stack", "Algorithms", "Data Structures", "System Design", "Web", "Soft Skills", "Security", "Live Labs"],
            topics: [
                {
                    title: "Live Architecture Demo",
                    category: "Live Labs",
                    desc: "Interactive diagnostic dashboard visualizing real-time order flow through Vert.x, Kafka, and Ignite.",
                    isExternalLink: true,
                    link: "java-demo"
                },
                {
                    title: "Big O Notation",
                    category: "Algorithms",
                    desc: "Quantifying the efficiency of algorithms as input size grows.",
                    studyTips: "Ignore constants. Focus on the dominant term.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Input Size (N)",
                        y: "Time / Operations",
                        quadrants: [
                            { label: "O(1) / O(log N)", color: "#22c55e", desc: "Excellent" },
                            { label: "O(N)", color: "#3b82f6", desc: "Fair" },
                            { label: "O(N log N)", color: "#eab308", desc: "Bad" },
                            { label: "O(N^2) / O(2^N)", color: "#ef4444", desc: "Horrible" }
                        ]
                    },
                    codeProblems: [
                        {
                            title: "Big O Examples",
                            complexity: "Varies",
                            desc: "Demonstrating O(1), O(N), and O(N^2) in both languages.",
                            javaCode: `// O(1) - Constant
public int getFirst(int[] arr) { return arr[0]; }

// O(N) - Linear
public void printAll(int[] arr) {
    for (int x : arr) System.out.println(x);
}

// O(N^2) - Quadratic
public void printPairs(int[] arr) {
    for (int x : arr)
    for (int y : arr) System.out.println(x + "," + y);
} `,
                            pythonCode: `# O(1) - Constant
def get_first(arr): return arr[0]

# O(N) - Linear
def print_all(arr):
for x in arr: print(x)

# O(N ^ 2) - Quadratic
def print_pairs(arr):
for x in arr:
    for y in arr: print(f"{x},{y}")`
                        }
                    ],
                    staticContent: [
                        "**O(1):** Constant time. Array access. Hash map lookup (avg).",
                        "**O(log N):** Logarithmic. Binary Search. Cutting the problem in half each step.",
                        "**O(N):** Linear. Looping through an array once.",
                        "**O(N log N):** Linearithmic. Efficient sorting (Merge Sort, Heap Sort).",
                        "**O(N^2):** Quadratic. Nested loops. Bubble Sort."
                    ]
                },
                {
                    title: "Graph Algorithms",
                    category: "Algorithms",
                    desc: "Connecting the dots. Graphs represent relationships between objects, like friends on social media or cities on a map.",
                    studyTips: "🌐 BFS is like ripples in a pond (layer by layer). DFS is like exploring a maze (go as deep as possible before turning back).",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Start Node" },
                            { label: "Visit Neighbor" },
                            { label: "Mark Visited" },
                            { label: "Queue/Stack" },
                            { label: "Repeat" }
                        ],
                        centerText: "Graphs"
                    },
                    staticContent: [
                        "**Adjacency List:** A map where each node lists its direct neighbors. Space efficient.",
                        "**BFS (Breadth-First):** Best for finding the absolute shortest path. Uses a **Queue**.",
                        "**DFS (Depth-First):** Best for exploring all possibilities or solving puzzles. Uses **Recursion**.",
                        "**Visited Set:** Critical to prevent going in circles (infinite loops).",
                        "**Complexity:** O(V + E) where V is vertices and E is edges."
                    ],
                    realWorldUsage: [
                        "**GPS & Maps:** Finding the quickest route between two cities.",
                        "**Social Networks:** 'People You May Know' recommendations based on mutual friends.",
                        "**Web Crawlers:** How Google discovers new websites by following links from page to page."
                    ],
                    codeProblems: [
                        {
                            title: "Graph Examples",
                            complexity: "O(V + E)",
                            desc: "Breadth-First Search (BFS) and Depth-First Search (DFS) patterns.",
                            javaCode: `// BFS - Layer by Layer
public void bfs(Node start) {
    Queue < Node > q = new LinkedList <> ();
    q.add(start); start.visited = true;
    while (!q.isEmpty()) {
        Node curr = q.poll();
        for (Node n : curr.neighbors) {
            if (!n.visited) { n.visited = true; q.add(n); }
        }
    }
}

// DFS - Deep First
public void dfs(Node curr) {
    if (curr == null || curr.visited) return;
    curr.visited = true;
    for (Node n : curr.neighbors) dfs(n);
} `,
                            pythonCode: `# BFS - Layer by Layer
from collections import deque
def bfs(start):
q = deque([start]); visited = { start }
while q:
    curr = q.popleft()
for n in curr.neighbors:
    if n not in visited: visited.add(n); q.append(n)

# DFS - Deep First
def dfs(curr, visited = None):
if visited is None: visited = set()
if curr in visited: return
visited.add(curr)
for n in curr.neighbors: dfs(n, visited)`
                        }
                    ]
                },
                {
                    title: "Common Interview Algorithms",
                    category: "Algorithms",
                    desc: "Standard 'tools' in a developer's belt. These patterns solve 80% of array and string interview questions.",
                    studyTips: "💡 Don't memorize the code; learn the pattern. Two-pointers is for scanning, HashMaps are for remembering.",
                    realWorldUsage: [
                        "**Search & Autocomplete:** Finding matching items instantly as a user types.",
                        "**Data Sanitization:** Cleaning up user input (like palindromes or formatting) before saving it.",
                        "**Financial Systems:** Finding pairs of transactions that sum to a specific target."
                    ],
                    codeProblems: [
                        {
                            title: "Algorithm Examples",
                            complexity: "Varies",
                            desc: "Demonstrating Two Sum, Is Palindrome, and Binary Search patterns.",
                            javaCode: `// Two Sum - O(N)
public int[] twoSum(int[] nums, int target) {
    Map < Integer, Integer > seen = new HashMap <> ();
    for (int i = 0; i < nums.length; i++) {
        int diff = target - nums[i];
        if (seen.containsKey(diff)) return new int[]{ seen.get(diff), i };
        seen.put(nums[i], i);
    }
    return new int[]{ };
}

// Valid Palindrome - O(N)
public boolean isPalindrome(String s) {
    int l = 0, r = s.length() - 1;
    while (l < r) {
        if (!Character.isLetterOrDigit(s.charAt(l))) l++;
        else if (!Character.isLetterOrDigit(s.charAt(r))) r--;
        else if (Character.toLowerCase(s.charAt(l)) != Character.toLowerCase(s.charAt(r))) return false;
        else { l++; r--; }
    }
    return true;
} `,
                            pythonCode: `# Two Sum - O(N)
def two_sum(nums, target):
seen = {}
for i, num in enumerate(nums):
    diff = target - num
if diff in seen: return [seen[diff], i]
seen[num] = i
return []

# Valid Palindrome - O(N)
def is_palindrome(s):
l, r = 0, len(s) - 1
while l < r:
    if not s[l].isalnum(): l += 1
        elif not s[r].isalnum(): r -= 1
        elif s[l].lower() != s[r].lower(): return False
        else: l += 1; r -= 1
return True`
                        }
                    ]
                },
                {
                    title: "Hash Tables",
                    category: "Data Structures",
                    desc: "Super-fast key-value storage. Think of it like a massive VIP coat check where you give a ticket (key) and get your coat (value) instantly without searching every rack.",
                    studyTips: "🔑 The 'Magic' is the Hash Function. It turns any key into a specific number (index). If two keys get the same number, it's a 'Collision'.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Key Input" },
                            { label: "Hash Function" },
                            { label: "Index Calculation" },
                            { label: "Store in Bucket" },
                            { label: "Handle Collision" }
                        ],
                        centerText: "Map"
                    },
                    staticContent: [
                        "**O(1) Average Time:** Instant lookups, insertions, and deletions.",
                        "**Hash Function:** Converts a key (string/int) into an array index.",
                        "**Collisions:** When two different keys hash to the same index.",
                        "**Chaining:** Storing a linked list at each index to handle multiple items.",
                        "**Load Factor:** When the map gets too 'crowded', it automatically resizes to stay fast."
                    ],
                    realWorldUsage: [
                        "**Database Indexing:** Finding a user profile by ID out of billions of records.",
                        "**Caching:** Storing expensive calculation results (like API responses) for instant reuse.",
                        "**Unique Counts:** Keeping track of unique visitors on a website in real-time."
                    ],
                    codeProblems: [
                        {
                            title: "Basic Hash Map",
                            complexity: "O(1) Avg",
                            desc: "Standard put/get operations.",
                            javaCode: `Map < String, Integer > map = new HashMap <> ();
map.put("Apple", 5);
int count = map.getOrDefault("Apple", 0); `,
                            pythonCode: `my_dict = { "Apple": 5 }
count = my_dict.get("Apple", 0)`
                        }
                    ]
                },
                {
                    title: "Linked Lists",
                    category: "Data Structures",
                    desc: "A chain of nodes where each node 'links' to the next. Unlike arrays, nodes don't need to live together in memory; they just need to know where their neighbor is.",
                    studyTips: "🚂 Like a train — you can't jump to carriage #5 without walking through #1-4 first. To add a new carriage in the middle, you just unhook one link and reconnect it.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Head Node" },
                            { label: "Data + Next Pointer" },
                            { label: "Traversal" },
                            { label: "Tail (Null)" }
                        ],
                        centerText: "Linked"
                    },
                    staticContent: [
                        "**Head:** The starting point. If you lose the head, you lose the whole list!",
                        "**Singly Linked:** Pointing forward (A -> B -> C).",
                        "**Doubly Linked:** Pointing both ways (A <-> B <-> C). Supports 'Back' button logic.",
                        "**O(1) Insertion:** No need to shift everything (like an array) when adding to the front.",
                        "**O(N) Search:** Finding an item takes longer because you must walk the chain."
                    ],
                    realWorldUsage: [
                        "**Music Playlists:** Songs linked in order (Next/Previous functionality).",
                        "**Browser History:** Moving back and forward through visited pages.",
                        "**Undo/Redo:** Storing a sequence of actions that can be traversed."
                    ],
                    codeProblems: [
                        {
                            title: "Linked List Examples",
                            complexity: "O(1) Head | O(N) Search",
                            desc: "Basic operations for adding and removing nodes.",
                            javaCode: `// LinkedList as a Deque
LinkedList < String > list = new LinkedList <> ();
list.addFirst("Head"); // O(1)
list.addLast("Tail");  // O(1)
String first = list.pollFirst(); `,
                            pythonCode: `# Using collections.deque for efficient LL
from collections import deque
ll = deque()
ll.appendleft("Head") # O(1)
ll.append("Tail")     # O(1)
ll.popleft()`
                        }
                    ]
                },
                {
                    title: "Stacks (LIFO)",
                    category: "Data Structures",
                    desc: "A vertical container where the last item put in is the first one taken out. Think of it as 'Last-In, First-Out'.",
                    studyTips: "🥞 Like a stack of pancakes — you always take from the top and add to the top. If you try to take the bottom one first, everything crashes!",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Top (Peek/Pop)", color: "#ef4444" },
                            { label: "Middle", color: "#3b82f6" },
                            { label: "Bottom (Push)", color: "#22c55e" }
                        ]
                    },
                    staticContent: [
                        "**Push:** Dropping an item onto the top. O(1).",
                        "**Pop:** Taking the top item off. O(1).",
                        "**Peek:** Looking at the top item without touching it. O(1).",
                        "**Backtracking:** Used by computers to 'remember' where they were (Recursion).",
                        "**LIFO:** Last-In, First-Out. The core rule of the stack."
                    ],
                    realWorldUsage: [
                        "**Undo Feature:** Your text editor pushes every change onto a stack. 'Undo' just pops the last change.",
                        "**Function Calls:** How your computer keeps track of which function to return to after a sub-task is finished.",
                        "**Balanced Parens:** Checking if brackets like `(([]))` are correctly closed in code."
                    ],
                    codeProblems: [
                        {
                            title: "Stack Examples",
                            complexity: "O(1) All Ops",
                            desc: "Standard Push/Pop patterns.",
                            javaCode: `Stack < Integer > stack = new Stack <> ();
stack.push(10);
stack.push(20);
int top = stack.pop(); // 20`,
                            pythonCode: `# Lists work perfectly as stacks
stack = []
stack.append(10)
stack.append(20)
top = stack.pop() # 20`
                        }
                    ]
                },
                {
                    title: "Queue & Deque (FIFO)",
                    category: "Data Structures",
                    desc: "A line where the first person to arrive is the first to be served. 'First-In, First-Out'.",
                    studyTips: "🎟️ Like a ticket line at a cinema — first person in line gets the ticket first. Deque is a fancy version where you can join or leave from both ends.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Rear (Enqueue)" },
                            { label: "Wait in Line" },
                            { label: "Front (Dequeue)" }
                        ],
                        centerText: "FIFO"
                    },
                    staticContent: [
                        "**Enqueue (Offer):** Joining the back of the line. O(1).",
                        "**Dequeue (Poll):** Leaving the front of the line. O(1).",
                        "**FIFO:** First-In, First-Out. Fairness is the core principle.",
                        "**Deque:** Double-Ended Queue. Can add/remove from Front and Back. O(1).",
                        "**Processing:** Used for tasks that need to be handled in order of arrival."
                    ],
                    realWorldUsage: [
                        "**Task Scheduling:** Managing background jobs (like sending emails) in order.",
                        "**Breadth-First Search (BFS):** Exploring a network layer by layer (finding the shortest path).",
                        "**Message Queues:** Kafka or RabbitMQ use queues to handle huge bursts of data safely."
                    ],
                    codeProblems: [
                        {
                            title: "Queue Examples",
                            complexity: "O(1) Enqueue/Dequeue",
                            desc: "Basic FIFO patterns.",
                            javaCode: `// Standard Queue
Queue<String> q = new LinkedList<>();
q.offer("First");
q.offer("Second");
String val = q.poll(); // "First"`,
                            pythonCode: `# collections.deque is preferred for queues
from collections import deque
q = deque()
q.append("First")
q.append("Second")
val = q.popleft() # "First"`
                        }
                    ]
                },
                {
                    title: "Sorting Algorithms",
                    category: "Algorithms",
                    desc: "Organizing the chaos. Sorting is about arranging data so you can find what you need instantly.",
                    studyTips: "⚖️ Merge Sort is 'Reliable' (Stable and O(N log N)). Quick Sort is usually 'Faster' in practice but can be 'Moody' (O(N^2) in rare cases).",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Efficient Sorting",
                        pillars: [
                            { label: "Merge Sort", desc: "Stable, O(N log N)" },
                            { label: "Quick Sort", desc: "Fast, O(N^2) worst" },
                            { label: "Heap Sort", desc: "In-place, O(N log N)" },
                            { label: "Tim Sort", desc: "Hybrid (Python/Java)" }
                        ],
                        foundation: "Divide & Conquer"
                    },
                    staticContent: [
                        "**Merge Sort:** Divide the list in half until you have single items, then merge them back in order. Very predictable.",
                        "**Quick Sort:** Pick a 'Pivot' and move smaller items to the left and larger to the right. Dynamic and efficient.",
                        "**Stability:** A 'Stable' sort keeps items with the same value in their original order (important for sorting by Name, then by Date).",
                        "**In-Place:** Sorting that doesn't need extra scratchpad memory (Heap Sort)."
                    ],
                    realWorldUsage: [
                        "**E-commerce Filters:** Sorting products by 'Price: Low to High' or 'Customer Rating'.",
                        "**Leaderboards:** Ranking players in a game by their high scores in real-time.",
                        "**File Managers:** Sorting your files by 'Last Modified' or 'Size' to find a document."
                    ],
                    codeProblems: [
                        {
                            title: "Sorting Examples",
                            complexity: "O(N log N)",
                            desc: "Efficient divide and conquer patterns (Merge & Quick Sort).",
                            javaCode: `// Quick Sort - Partition pattern
public void quickSort(int[] arr, int low, int high) {
    if (low < high) {
        int pi = partition(arr, low, high);
        quickSort(arr, low, pi - 1);
        quickSort(arr, pi + 1, high);
    }
}

// Merge Sort - Divide & Conquer
public void mergeSort(int[] arr, int n) {
    if (n < 2) return;
    int mid = n / 2;
    // ... splitting and merging logic
}`,
                            pythonCode: `# Quick Sort - Concise list comprehension
def quick_sort(arr):
    if len(arr) <= 1: return arr
    pivot = arr[len(arr) // 2]
    l = [x for x in arr if x < pivot]
    m = [x for x in arr if x == pivot]
    r = [x for x in arr if x > pivot]
    return quick_sort(l) + m + quick_sort(r)`
                        }
                    ]
                },
                {
                    title: "CAP Theorem",
                    category: "System Design",
                    desc: "The 'Pick Two' rule for distributed databases. You can't have everything at once when things break.",
                    studyTips: "🏦 Think of it like a bank. If the New York and London branches can't talk, do they stop taking withdrawals (Availability) or risk allowing double-spending (Consistency)?",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "CP (Banking)", color: "#a855f7" },
                            { label: "AP (Social Feed)", color: "#0ea5e9" },
                            { label: "CA (Theoretical)", color: "#64748b" }
                        ]
                    },
                    staticContent: [
                        "**Consistency (C):** Every user sees the same data at the same time. No 'stale' data.",
                        "**Availability (A):** The system always answers, even if it might be slightly old data.",
                        "**Partition Tolerance (P):** The system keeps working even if the network between servers fails.",
                        "**The Choice:** In the real world, network errors (P) *will* happen. You must choose between C and A."
                    ],
                    realWorldUsage: [
                        "**Banking Systems (CP):** Must have Consistency. Better to show an error than a wrong balance.",
                        "**Social Media Feeds (AP):** Prefer Availability. It's okay if a post appears a few seconds late, as long as the site isn't 'down'.",
                        "**Distributed Locks:** Systems like Zookeeper prioritize Consistency to ensure only one person holds a lock at a time."
                    ]
                },
                {
                    title: "Caching Strategies",
                    category: "System Design",
                    desc: "Using a 'Short-Term Memory' to avoid slow trips to the main database.",
                    studyTips: "🚀 Caching is the #1 way to make a slow app feel fast. But remember: 'There are only two hard things in Computer Science: cache invalidation and naming things.'",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Request Data" },
                            { label: "Check Cache" },
                            { label: "Cache Miss?" },
                            { label: "Fetch DB" },
                            { label: "Update Cache" }
                        ],
                        centerText: "Cache"
                    },
                    staticContent: [
                        "**Cache Aside:** Look in cache first; if empty, fetch from DB and save it in cache. (Most common).",
                        "**Write-Through:** Update both cache and DB at the same time. Always consistent, but slightly slower writes.",
                        "**Write-Back:** Update cache immediately, then update the DB later in the background. Super fast but risky.",
                        "**Eviction (LRU):** When the cache is full, kick out the 'Least Recently Used' item."
                    ],
                    realWorldUsage: [
                        "**CDNs (Cloudflare):** Storing images and videos closer to the user for instant loading.",
                        "**User Sessions:** Storing 'Logged In' status in Redis so you don't query the DB on every single click.",
                        "**API Rate Limiting:** Tracking how many requests a user has made per minute to prevent abuse."
                    ],
                    codeProblems: [
                        {
                            title: "Cache Aside Pattern",
                            complexity: "O(1) Cache | O(1) DB",
                            desc: "The most common caching pattern for heavy reads.",
                            javaCode: `public Object getData(String key) {
    Object val = cache.get(key);
    if (val != null) return val;
    val = db.fetch(key);
    if (val != null) cache.put(key, val);
    return val;
}`,
                            pythonCode: `def get_data(key):
    val = cache.get(key)
    if val: return val
    val = db.fetch(key)
    if val: cache.set(key, val)
    return val`
                        }
                    ]
                },
                {
                    title: "Database Scaling",
                    category: "System Design",
                    desc: "How to handle going from 1,000 users to 1 Billion users without the database exploding.",
                    studyTips: "📈 Start simple (Vertical). Then add Read Replicas. Only go to Sharding when you absolutely must — it's powerful but complex!",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Database Scaling",
                        pillars: [
                            { label: "Vertical", desc: "Bigger Server" },
                            { label: "Horizontal", desc: "Sharding" },
                            { label: "Replication", desc: "Read Replicas" },
                            { label: "Partitioning", desc: "Data Splitting" }
                        ]
                    },
                    staticContent: [
                        "**Vertical Scaling:** Getting a bigger, faster computer. (Expensive and has a limit).",
                        "**Read Replicas:** Having one server for 'Writes' and many for 'Reads'. (Perfect for social media).",
                        "**Sharding:** Splitting one big table into many small pieces across different servers.",
                        "**Consistent Hashing:** A smart way to add new servers without moving all your data around."
                    ],
                    realWorldUsage: [
                        "**Global Games:** Sharding users by region (Asia servers vs Europe servers) to reduce lag.",
                        "**E-commerce Giants:** Using Read Replicas for product listings (lots of reads) and a master DB for orders (critical writes).",
                        "**High-Traffic APIs:** Sharding by API Key to ensure one busy customer doesn't slow down everyone else."
                    ],
                    codeProblems: [
                        {
                            title: "Simple Sharding Logic",
                            complexity: "O(1) Route",
                            desc: "Routing a request to a database shard based on User ID.",
                            javaCode: `public DataSource getShard(long userId) {
    int shardId = (int) (userId % shardCount);
    return shards.get(shardId);
}`,
                            pythonCode: `def get_shard(user_id):
    shard_id = user_id % shard_count
    return shards[shard_id]`
                        }
                    ]
                },
                {
                    title: "SOLID Principles",
                    category: "Soft Skills",
                    desc: "The five commandments of clean code. They ensure your software is easy to change and hard to break.",
                    studyTips: "🏗️ Think of it like building with Lego blocks rather than molding a single piece of clay. With Lego, you can swap any piece without destroying the whole castle.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Maintainable Code",
                        pillars: [
                            { label: "Single Resp", desc: "SRP" },
                            { label: "Open/Closed", desc: "OCP" },
                            { label: "Liskov Subst", desc: "LSP" },
                            { label: "Interface Seg", desc: "ISP" }
                        ],
                        foundation: "Dependency Inv (DIP)"
                    },
                    staticContent: [
                        "**S (Single Responsibility):** A class should do one thing and do it well.",
                        "**O (Open/Closed):** You should be able to add new features without changing old code.",
                        "**L (Liskov):** Any child class should be able to stand in for its parent without breaking things.",
                        "**I (Interface Segregation):** Don't force a class to implement methods it doesn't need.",
                        "**D (Dependency Inversion):** High-level logic shouldn't depend on low-level details. Both should use abstractions (Interfaces)."
                    ],
                    realWorldUsage: [
                        "**Plugin Systems:** Adding new payment methods (Stripe, PayPal) without touching the main checkout code.",
                        "**Unit Testing:** Swapping a real database for a 'Mock' database during tests using Dependency Injection.",
                        "**UI Components:** Building reusable buttons that work anywhere by accepting generic properties."
                    ]
                },
                {
                    title: "Critical Rendering Path",
                    category: "Web",
                    desc: "The journey from a URL to a visible page. It's the sequence of steps the browser takes to turn HTML/CSS into pixels.",
                    studyTips: "⚡ Performance is all about the Path. Every large image or heavy script is a 'Speed Bump' that keeps the screen blank for longer.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "HTML -> DOM" },
                            { label: "CSS -> CSSOM" },
                            { label: "Render Tree" },
                            { label: "Layout" },
                            { label: "Paint" }
                        ],
                        centerText: "Render"
                    },
                    staticContent: [
                        "**DOM:** The browser builds the 'Skeleton' from your HTML tags.",
                        "**CSSOM:** The browser applies 'Skin' and 'Clothes' from your CSS styles.",
                        "**Render Tree:** The browser combines them, ignoring things like `display: none`.",
                        "**Layout:** Calculating the exact math of where everything goes on the screen.",
                        "**Paint:** Filling in the actual pixels, colors, and shadows."
                    ],
                    realWorldUsage: [
                        "**Web Vitals Performance:** Optimizing 'Largest Contentful Paint' (LCP) to make pages feel instant.",
                        "**Smooth Animations:** Using `transform` instead of `width/height` to avoid expensive 'Layout' steps.",
                        "**Code Splitting:** Loading only the JavaScript needed for the first screen to clear the path faster."
                    ]
                },
                {
                    title: "REST vs GraphQL",
                    category: "Web",
                    desc: "Comparing architectural styles for API design.",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Flexibility",
                        y: "Caching Ability",
                        quadrants: [
                            { label: "GraphQL", color: "#a855f7", desc: "High Flex / Hard Cache" },
                            { label: "REST", color: "#22c55e", desc: "Low Flex / Easy Cache" },
                            { label: "RPC", color: "#94a3b8", desc: "Low Flex / No Cache" },
                            { label: "Falcor", color: "#eab308", desc: "Niche" }
                        ]
                    },
                    staticContent: [
                        "**REST:** Multiple endpoints (/users, /posts). Over-fetching (getting too much data) and Under-fetching (need more requests). Good caching via HTTP.",
                        "**GraphQL:** Single endpoint. Client specifies exactly what data it needs. Solves over/under fetching. Harder to cache network responses.",
                        "**Schema:** GraphQL uses a strong type system. REST is looser (usually JSON).",
                        "**Versioning:** REST uses v1/v2. GraphQL avoids versioning by deprecating fields."
                    ]
                },
                {
                    title: "Testing Pyramid",
                    category: "Soft Skills",
                    desc: "The blueprint for a bug-free app. It tells you where to spend your testing time and effort.",
                    studyTips: "🧁 Avoid the 'Ice Cream Cone' trap! Don't have thousands of slow, flaky UI tests (the top) and no fast, reliable core tests (the bottom).",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "E2E (UI Tests)", color: "#ef4444" },
                            { label: "Integration Tests", color: "#eab308" },
                            { label: "Unit Tests", color: "#22c55e" }
                        ]
                    },
                    staticContent: [
                        "**Unit Tests:** Testing a single brick. Fast, cheap, and you need lots of them. (Base of the pyramid).",
                        "**Integration:** Testing how two bricks fit together (e.g., App + Database).",
                        "**E2E (End-to-End):** Testing the entire building from the user's perspective. Slow and expensive.",
                        "**Goal:** Fast feedback. 70% Unit, 20% Integration, 10% E2E."
                    ],
                    realWorldUsage: [
                        "**CI/CD Safety:** Automatically running Unit Tests on every code save to catch bugs instantly.",
                        "**Payment Flows:** Using E2E tests to ensure a user can actually finish a purchase without errors.",
                        "**Regression Testing:** Making sure new features don't accidentally break old features."
                    ]
                },
                {
                    title: "CI/CD Pipeline",
                    category: "Soft Skills",
                    desc: "Automating the delivery of software to production.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Commit" },
                            { label: "Build" },
                            { label: "Test" },
                            { label: "Deploy Staging" },
                            { label: "Deploy Prod" }
                        ],
                        centerText: "Auto"
                    },
                    staticContent: [
                        "**Continuous Integration:** Merging code frequently and running automated tests.",
                        "**Continuous Delivery:** Code is always in a deployable state.",
                        "**Continuous Deployment:** Changes go live automatically without human intervention.",
                        "**Blue/Green Deployment:** Zero downtime. Switch traffic between old (Blue) and new (Green) versions."
                    ]
                },
                {
                    title: "System Design Masterclass",
                    category: "System Design",
                    desc: "The Ultimate System Design Lifecycle & Component Flow. A comprehensive roadmap for architecting scalable systems.",
                    hasSystemDesignLifecycle: true,
                    studyTips: "🏆 Master the flow: Requirements ➔ High-Level ➔ Detailed ➔ Ops. Don't forget cross-cutting concerns like Security and Concurrency!",
                    staticContent: [
                        "**Phase 1: Requirements:** Gathering functional and non-functional goals. Back-of-the-envelope estimates.",
                        "**Phase 2: High-Level:** Defining boundaries, communication patterns (REST/gRPC/Messaging), and architecture styles.",
                        "**Phase 3: Detailed Design:** Deep dive into DB modeling, CAP theorem, Caching, Scaling, and Fault Tolerance.",
                        "**Phase 4: Ops & Maintenance:** Observability (Metrics/Logs/Tracing) and CI/CD pipelines."
                    ]
                },
                {
                    title: "System Design Interview",
                    category: "System Design",
                    desc: "A framework for acing the system design interview.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Requirements" },
                            { label: "Estimation" },
                            { label: "High Level" },
                            { label: "Deep Dive" },
                            { label: "Wrap Up" }
                        ],
                        centerText: "Design"
                    },
                    staticContent: [
                        "**Requirements:** Functional (What it does) & Non-Functional (CAP, Scale).",
                        "**Estimation:** QPS (Queries Per Second), Storage, Bandwidth. 1 Million requests/day ~= 12 RPS.",
                        "**High Level Design:** Load Balancer, Web Server, Database, Cache.",
                        "**Deep Dive:** Focus on the interesting parts (Sharding key, Algorithm, Data model)."
                    ]
                },
                {
                    title: "Security Basics",
                    category: "Security",
                    desc: "Fundamental concepts for writing secure code.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Secure App",
                        pillars: [
                            { label: "AuthN", desc: "Identity" },
                            { label: "AuthZ", desc: "Permissions" },
                            { label: "Encryption", desc: "Data Safety" },
                            { label: "Validation", desc: "Input Check" }
                        ],
                        foundation: "Threat Model"
                    },
                    staticContent: [
                        "**OWASP Top 10:** The most common web vulnerabilities (Injection, Broken Auth, XSS).",
                        "**Sanitization:** Never trust user input. Prevent SQL Injection and XSS.",
                        "**Least Privilege:** Give users/processes only the permissions they absolutely need.",
                        "**HTTPS:** Encrypt data in transit using TLS."
                    ]
                },
                {
                    title: "Tree Data Structures",
                    category: "Data Structures",
                    desc: "A hierarchical structure that branches out, like a family tree or the folder system on your computer.",
                    studyTips: "🌳 Binary Trees are the most common. 'Binary' just means each parent can have at most 2 children. Search Trees (BST) keep them in order.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Red-Black / AVL (Self-Balancing)", color: "#a855f7" },
                            { label: "BST (Binary Search Tree)", color: "#3b82f6" },
                            { label: "Binary Tree", color: "#64748b" }
                        ]
                    },
                    staticContent: [
                        "**Root:** The top node of the tree.",
                        "**Leaf:** A node with no children (the endpoints).",
                        "**BST Rule:** Left child < Parent < Right child. This makes searching super fast.",
                        "**O(log N):** The time it takes to find an item if the tree is balanced.",
                        "**Self-Balancing:** Advanced trees (AVL) reshuffle themselves to stay flat and fast."
                    ],
                    realWorldUsage: [
                        "**File Systems:** How your computer organizes folders and files into a hierarchy.",
                        "**HTML DOM:** How browsers represent the structure of a webpage.",
                        "**Database Indexing:** B-Trees allow databases to find a single row among millions in milliseconds."
                    ]
                },
                {
                    title: "Vert.x (Reactive Microservices)",
                    category: "Java Stack",
                    desc: "High-performance, non-blocking toolkit for building reactive applications on the JVM.",
                    studyTips: "🍕 Think of Vert.x like a single pizza chef who takes orders (requests) and never blocks — while the oven bakes (async IO), the chef takes the next order.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Event Loop" },
                            { label: "Verticle Receives" },
                            { label: "Non-Blocking Op" },
                            { label: "Handler Callback" },
                            { label: "Event Bus Reply" }
                        ],
                        centerText: "Vert.x"
                    },
                    staticContent: [
                        "**Event Loop:** One thread handles thousands of connections. Never block it!",
                        "**Verticle:** Your unit of code. Standard Verticles run on the Event Loop.",
                        "**Worker Verticle:** For blocking operations (JDBC, heavy CPU) to avoid freezing the Loop.",
                        "**Event Bus:** Internal post office for verticle-to-verticle communication.",
                        "**vs Spring MVC:** Traditional servers use 1 thread per request; Vert.x uses 1 for all I/O."
                    ],
                    codeProblems: [
                        {
                            title: "Worker Verticle for Blocking Ops",
                            complexity: "Event-Driven",
                            desc: "Running blocking tasks without stopping the main Event Loop.",
                            javaCode: `public class ReportWorker extends AbstractVerticle {
  public void start() {
    vertx.eventBus().consumer("generate.report", msg -> {
      String pdf = pdfLib.generate(); // blocking is OK in Worker
      msg.reply(pdf);
    });
  }
}`,
                            pythonCode: `import asyncio

async def handle_report(msg):
    # Run blocking PDF generation in a thread pool
    loop = asyncio.get_running_loop()
    pdf = await loop.run_in_executor(None, pdf_lib.generate)
    msg.reply(pdf)`
                        }
                    ]
                },
                {
                    title: "Spring Boot & RESTful Microservices",
                    category: "Java Stack",
                    desc: "Production-ready framework that auto-configures Spring applications with minimal boilerplate.",
                    studyTips: "🏗️ Spring Boot is like a building kit—it auto-configs based on the 'jars' (bricks) you have in your toolbox.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Production-Ready App",
                        pillars: [
                            { label: "Auto-Config", desc: "Zero XML" },
                            { label: "Embedded Server", desc: "Tomcat/Netty" },
                            { label: "Actuator", desc: "Health/Metrics" },
                            { label: "Spring Data", desc: "JPA/Repos" }
                        ],
                        foundation: "Spring Core (IoC / DI)"
                    },
                    staticContent: [
                        "**Auto-Configuration:** Reads spring.factories to set up beans based on classpath jars.",
                        "**Constructor Injection:** Preferred over @Autowired for testability and immutability.",
                        "**Actuator:** Exposes /health and /metrics for Kubernetes monitoring.",
                        "**Profiles:** Manage environment-specific configs (@Profile('prod')).",
                        "**Resiliency:** Use Resilience4j (Circuit Breaker, Retry) for fault-tolerant services."
                    ],
                    codeProblems: [
                        {
                            title: "REST Controller with DI",
                            complexity: "O(1) Route",
                            desc: "Clean controller design using constructor injection.",
                            javaCode: `@RestController
@RequestMapping("/api/v1/orders")
public class OrderController {
  private final OrderService service;
  public OrderController(OrderService service) { this.service = service; }

  @GetMapping("/{id}")
  public ResponseEntity<OrderDTO> get(@PathVariable Long id) {
    return service.findById(id).map(ResponseEntity::ok).orElse(ResponseEntity.notFound().build());
  }
}`,
                            pythonCode: `from fastapi import FastAPI, HTTPException
app = FastAPI()

@app.get("/api/v1/orders/{order_id}")
async def get_order(order_id: int):
    order = await service.find_by_id(order_id)
    if not order:
        raise HTTPException(status_code=404)
    return order`
                        }
                    ]
                },
                {
                    title: "Apache Kafka (Event Streaming)",
                    category: "Java Stack",
                    desc: "High-throughput distributed event streaming platform for real-time data pipelines.",
                    studyTips: "📰 Kafka is like a newspaper printing press. Publishers print stories into sections (topics). Multiple subscribers each read their own copy—the paper isn't 'consumed' and disappears.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Producer Sends" },
                            { label: "Broker / Topic" },
                            { label: "Partition & Replicate" },
                            { label: "Consumer Group Reads" },
                            { label: "Commit Offset" }
                        ],
                        centerText: "Kafka"
                    },
                    staticContent: [
                        "**Topic:** Divided into Partitions for parallelism. Messages in a partition are always in order.",
                        "**Consumer Group:** Each partition is owned by exactly one consumer in the group.",
                        "**Offset:** The 'bookmark' where a consumer stopped reading.",
                        "**At-Least-Once:** Commit offset AFTER processing. Handle duplicates with idempotent consumers.",
                        "**Scaling:** Increase partitions to add more consumers for higher throughput."
                    ],
                    codeProblems: [
                        {
                            title: "Kafka Producer & Consumer",
                            complexity: "Distributed",
                            desc: "Basic message production and listener-based consumption.",
                            javaCode: `// Producer
kafka.send("orders.placed", orderId, new OrderEvent(order));

// Consumer
@KafkaListener(topics = "orders.placed", groupId = "inventory-service")
public void reserveStock(List<OrderEvent> events, Acknowledgment ack) {
  events.forEach(e -> inventory.reserve(e.getItems()));
  ack.acknowledge();
}`,
                            pythonCode: `from confluent_kafka import Producer, Consumer

# Producer
p = Producer({'bootstrap.servers': 'localhost:9092'})
p.produce('orders.placed', key=str(order_id), value=order_json)
p.flush()

# Consumer
c = Consumer({'bootstrap.servers': 'localhost:9092', 'group.id': 'inventory-service'})
c.subscribe(['orders.placed'])
while True:
    msg = c.poll(1.0)
    if msg:
        process(msg.value())
        c.commit()`
                        }
                    ]
                },
                {
                    title: "Apache Ignite (In-Memory Computing)",
                    category: "Java Stack",
                    desc: "Distributed in-memory database and computing platform for sub-millisecond data access.",
                    studyTips: "🏎️ Ignite is like a racing pit crew with a shared memory board. Specs are memorized instead of fetched from a binder in the garage (disk).",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "In-Memory Data Grid",
                        pillars: [
                            { label: "Distributed Cache", desc: "Key-Value Store" },
                            { label: "SQL Queries", desc: "ANSI SQL on RAM" },
                            { label: "Compute Grid", desc: "Collocate Compute" },
                            { label: "Persistence", desc: "Native / 3rd Party" }
                        ],
                        foundation: "Distributed Data Partitioning"
                    },
                    staticContent: [
                        "**Affinity Colocation:** Run code ON the same node where the data exists to avoid network hops.",
                        "**Data Partitioning:** Each node owns a percentage of keys. Backups ensure fault tolerance.",
                        "**Near Cache:** Local client-side copy of hot keys for zero-network reads.",
                        "**Distributed SQL:** Run ANSI SQL queries across the entire cluster RAM simultaneously.",
                        "**ACID Transactions:** Full support for distributed transactions across multiple keys and nodes."
                    ],
                    codeProblems: [
                        {
                            title: "Distributed SQL Query",
                            complexity: "O(log N) RAM",
                            desc: "Querying distributed in-memory data using SQL.",
                            javaCode: `SqlFieldsQuery query = new SqlFieldsQuery("SELECT name FROM Product WHERE price > ?");
productCache.query(query.setArgs(100)).forEach(row -> System.out.println(row));`,
                            pythonCode: `from pyignite import Client
client = Client()
client.connect('127.0.0.1', 10800)
my_cache = client.get_or_create_cache('products')

result = client.sql("SELECT name FROM Product WHERE price > 100")
for row in result:
    print(row)`
                        }
                    ]
                },
                {
                    title: "Apache Spark (Big Data Processing)",
                    category: "Java Stack",
                    desc: "Unified analytics engine for large-scale data processing in-memory.",
                    studyTips: "📦 Spark is like a warehouse sorting system. Sorters go to each aisle simultaneously (parallel) rather than bringing everything to one desk.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Load Data (RDD/DF)" },
                            { label: "Transformations (Lazy)" },
                            { label: "Action Triggered" },
                            { label: "DAG Optimizer" },
                            { label: "Executor Tasks" }
                        ],
                        centerText: "Spark"
                    },
                    staticContent: [
                        "**Lazy Evaluation:** Transformations (map, filter) do nothing until an Action (count, write) is called.",
                        "**Narrow Transformation:** (filter, map) Stay on the same node. Fast.",
                        "**Wide Transformation:** (groupBy, join) Cause a 'Shuffle' across the network. Slow.",
                        "**RDD vs DataFrame:** RDD is low-level; DataFrame is optimized by the Catalyst engine.",
                        "**Data Skew:** When one partition is much larger than others, causing bottlenecks. Fix with salting."
                    ],
                    codeProblems: [
                        {
                            title: "DataFrame Revenue Aggregation",
                            complexity: "Parallel O(N)",
                            desc: "Filtering and grouping petabytes of data using functional transformations.",
                            javaCode: `Dataset<Row> revenue = orders.filter(col("status").equalTo("COMPLETE"))
    .groupBy("category")
    .agg(sum("amount").as("total"));`,
                            pythonCode: `revenue = orders.filter(orders.status == "COMPLETE") \\
    .groupBy("category") \\
    .agg(sum("amount").alias("total"))`
                        }
                    ]
                },
                {
                    title: "PostgreSQL (Relational DB)",
                    category: "Java Stack",
                    desc: "Persistent relational database focusing on ACID compliance and complex queries.",
                    studyTips: "📚 PostgreSQL is like a library with a card catalog (indexes). Without indexes, the librarian (DB) must read every book title (Sequential Scan) to find yours.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "ACID Compliance",
                        pillars: [
                            { label: "Atomicity", desc: "All or Nothing" },
                            { label: "Consistency", desc: "Valid State" },
                            { label: "Isolation", desc: "Concurrent Txn" },
                            { label: "Durability", desc: "Committed = Safe" }
                        ],
                        foundation: "B-Tree / GIN / GiST Indexes"
                    },
                    staticContent: [
                        "**ACID:** Standards ensure data integrity even during crashes or errors.",
                        "**MVCC:** Readers never block writers. Multi-Version Concurrency Control keeps old row versions until VACUUM.",
                        "**Index Strategy:** Use B-Tree for ranges, GIN for JSONB and full-text search.",
                        "**EXPLAIN ANALYZE:** The essential tool to see how the query planner executes your SQL.",
                        "**JSONB:** Store and index semi-structured data within a strictly relational schema."
                    ],
                    codeProblems: [
                        {
                            title: "Indexed Query with JPA",
                            complexity: "O(log N)",
                            desc: "Standard transactional query using Spring Data JPA.",
                            javaCode: `@Query("SELECT * FROM orders WHERE status = :status")
List<Order> findByStatus(String status);`,
                            pythonCode: `# SQLAlchemy ORM
orders = session.query(Order).filter(Order.status == status).all()`
                        }
                    ]
                },
                {
                    title: "Cassandra (NoSQL Column Store)",
                    category: "Java Stack",
                    desc: "Highly available, distributed NoSQL database designed for massive write throughput.",
                    studyTips: "🏪 Cassandra is like a 24/7 convenience store chain. Each store has copies of items. If one burns down, others serve customers. But you can only ask store-specific questions (partition keys).",
                    diagramType: "quadrant",
                    diagramData: {
                        x: "Consistency",
                        y: "Availability",
                        quadrants: [
                            { label: "Tunable (AP/CP)", color: "#6366f1", desc: "ONE / QUORUM / ALL" },
                            { label: "PostgreSQL (CP)", color: "#22c55e", desc: "Always consistent" },
                            { label: "DynamoDB (AP)", color: "#eab308", desc: "Eventually consistent" },
                            { label: "Cassandra (AP)", color: "#a855f7", desc: "Tunable consistency" }
                        ]
                    },
                    staticContent: [
                        "**Query-Driven Modeling:** Design schema PER QUERY, not per entity. Denormalize everything.",
                        "**Partition Key:** Determines node location. MUST be in the WHERE clause.",
                        "**Tunable Consistency:** Choose speed (ONE) or safety (QUORUM/ALL) per request.",
                        "**No JOINs:** Data must be duplicated across tables to support different access patterns.",
                        "**Hot Partition:** Avoid frequent keys (like dates) that overwhelm a single node."
                    ],
                    codeProblems: [
                        {
                            title: "Denormalized Query",
                            complexity: "O(1) Partition",
                            desc: "Fast lookup by partition key without JOINs.",
                            javaCode: `// Cassandra CQL (NoSQL)
SELECT * FROM orders_by_user WHERE user_id = ? LIMIT 20;`,
                            pythonCode: `query = "SELECT * FROM orders_by_user WHERE user_id = %s LIMIT 20"
rows = session.execute(query, [user_id])
for row in rows:
    print(row.amount)`
                        }
                    ]
                },
                {
                    title: "Yellowbrick (Analytical Data Warehouse)",
                    category: "Java Stack",
                    desc: "Hybrid cloud data warehouse for sub-second SQL analytics at petabyte scale.",
                    studyTips: "📊 Yellowbrick is like a fast accountant with a massive spreadsheet. PostgreSQL is your cash register (transactions); Yellowbrick answers 'What were total sales last quarter?' in seconds.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "BI & Analytics (Yellowbrick)", color: "#f59e0b" },
                            { label: "Data Processing (Spark)", color: "#6366f1" },
                            { label: "Streaming (Kafka)", color: "#3b82f6" },
                            { label: "Operational DBs (PG / Cassandra)", color: "#22c55e" }
                        ]
                    },
                    staticContent: [
                        "**OLAP vs OLTP:** Optimized for complex aggregations (SUM, AVG) over billions of rows.",
                        "**Columnar Storage:** Reads ONLY the columns needed for a query, 100x less I/O than row stores.",
                        "**MPP Architecture:** Massively Parallel Processing splits queries across all nodes simultaneously.",
                        "**Data Flow:** Typically Kafka -> Spark ETL -> Yellowbrick -> BI Tools (Tableau/Looker).",
                        "**Mixed Workloads:** Handles high concurrency of users and heavy analytical queries together."
                    ],
                    codeProblems: [
                        {
                            title: "Analytical Window Function",
                            complexity: "Parallel OLAP",
                            desc: "Performing rolling aggregations over massive datasets.",
                            javaCode: `-- ANSI SQL - Rolling 7-day revenue
SELECT category, event_date, SUM(revenue) OVER (
    PARTITION BY category ORDER BY event_date
    ROWS BETWEEN 6 PRECEDING AND CURRENT ROW
) as rolling_7d FROM sales;`,
                            pythonCode: `# In PySpark (common ETL to Yellowbrick)
from pyspark.sql import Window
w = Window.partitionBy("category").orderBy("event_date").rowsBetween(-6, 0)
df.withColumn("rolling_7d", sum("revenue").over(w))`
                        }
                    ]
                },
                {
                    title: "Object Oriented Programming (OOP)",
                    category: "Java Stack",
                    desc: "The standard way to build complex systems by breaking them down into 'Objects' that interact with each other.",
                    studyTips: "🏛️ OOP is like a city building plan. You have blueprints (Classes) and actual buildings (Objects). Rules (Interfaces) ensure every building follows the city code.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Clean Code Architecture",
                        pillars: [
                            { label: "Encapsulation", desc: "Private Data" },
                            { label: "Inheritance", desc: "Code Reuse" },
                            { label: "Polymorphism", desc: "Many Forms" },
                            { label: "Abstraction", desc: "Hide Detail" }
                        ],
                        foundation: "SOLID Principles"
                    },
                    staticContent: [
                        "**Encapsulation:** Keeping data safe inside an object and only exposing what's necessary (Private fields + Getters/Setters).",
                        "**Inheritance:** Reusing code by letting one class inherit the features of another (A Dog *is-a* Mammal).",
                        "**Polymorphism:** The ability of different objects to respond to the same command in their own way (A Cat and Dog both 'Speak', but differently).",
                        "**Abstraction:** Focusing on 'What' as opposed to 'How' (You know how to drive a car without knowing how the engine works).",
                        "**Composition:** Building complex objects out of simpler ones (A Car *has-a* Engine)."
                    ],
                    realWorldUsage: [
                        "**Game Development:** Every character, weapon, and item is an object with its own rules and behaviors.",
                        "**GUI Frameworks:** Buttons, TextBoxes, and Windows are all objects that can be styled and manipulated.",
                        "**Simulations:** Modeling real-world physics or traffic systems using autonomous agents."
                    ],
                    codeProblems: [
                        {
                            title: "OOP Examples",
                            complexity: "Varies",
                            desc: "Demonstrating Encapsulation, Polymorphism, and Abstraction patterns.",
                            javaCode: `abstract class SmartDevice {
    private String name; // Encapsulation
    protected boolean isOn;

    public SmartDevice(String name) { this.name = name; }
    public String getName() { return name; }
    public abstract void performAction(); // Abstraction
}

class Light extends SmartDevice { // Inheritance
    @Override
    public void performAction() { // Polymorphism
        isOn = true;
        System.out.println(getName() + " is shining!");
    }
}`,
                            pythonCode: `from abc import ABC, abstractmethod

class SmartDevice(ABC):
    def __init__(self, name):
        self._name = name # Encapsulation
        self._is_on = False

    @abstractmethod
    def perform_action(self): # Abstraction
        pass

class Light(SmartDevice): # Inheritance
    def perform_action(self): # Polymorphism
        self._is_on = True
        print(f"{self._name} is shining!")`
                        }
                    ]
                },
                {
                    title: "Java Collections Framework",
                    category: "Java Stack",
                    desc: "The 'Toolbox' of Java. A standardized way to store and manipulate groups of objects (Lists, Sets, and Maps).",
                    studyTips: "🧺 Choose the right basket! Use a **List** if order matters, a **Set** if you need uniqueness, and a **Map** for lightning-fast lookups.",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Map (HashMap / TreeMap)", color: "#a855f7" },
                            { label: "Set (HashSet / TreeSet)", color: "#8b5cf6" },
                            { label: "List (ArrayList / LinkedList)", color: "#6366f1" }
                        ]
                    },
                    staticContent: [
                        "**ArrayList:** Like a dynamic array that grows as needed. Great for reading data.",
                        "**HashMap:** Key-value pairs with O(1) performance. Like a dictionary.",
                        "**HashSet:** A collection that prevents duplicates (e.g., a list of unique usernames).",
                        "**Sorting:** Collections.sort() uses TimSort for high-speed organization.",
                        "**Choice:** Picking the wrong collection can make an app 100x slower. Always consider your goal (Search? Sort? Insert?)."
                    ],
                    realWorldUsage: [
                        "**E-commerce Carts:** Using an ArrayList to store the items a customer has added in order.",
                        "**Unique Users:** Using a HashSet to track which users have clicked a specific link to avoid double-counting.",
                        "**Phonebook:** Using a HashMap to look up a person's phone number by their name instantly."
                    ],
                    codeProblems: [
                        {
                            title: "Collection Examples",
                            complexity: "Varies",
                            desc: "Standard patterns using HashMap and ArrayList for efficient data handling.",
                            javaCode: `// Frequency Map - O(N)
Map<String, Integer> counts = new HashMap<>();
for (String word : words) {
    counts.put(word, counts.getOrDefault(word, 0) + 1);
}`,
                            pythonCode: `# Frequency Map - O(N)
from collections import Counter
counts = Counter(words)`
                        }
                    ]
                },
                {
                    title: "Java Concurrency & Multithreading",
                    category: "Java Stack",
                    desc: "Teaching your program to multi-task. It's about running many operations at once to make better use of modern CPUs.",
                    studyTips: "🧵 Concurrency is like a professional kitchen with 10 chefs. If they all try to use the same stove (variable) at once, disaster happens! Locks ensure safe sharing.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "New / Runnable" },
                            { label: "Running" },
                            { label: "Blocked / Waiting" },
                            { label: "Terminated" }
                        ],
                        centerText: "Thread State"
                    },
                    staticContent: [
                        "**Atomic Ops:** Simple, fast ways to update numbers without needing slow 'locks'.",
                        "**Thread Pool:** Instead of hiring a new chef for every order, you keep a 'Ready' team of workers (ExecutorService).",
                        "**Deadlock:** When two threads are stuck waiting for each other in a Mexican standoff.",
                        "**Thread-Safety:** Ensuring your code doesn't produce wrong answers when many people use it at once.",
                        "**Virtual Threads:** A massive performance boost in Java 21, allowing you to run millions of 'light' threads."
                    ],
                    realWorldUsage: [
                        "**Web Servers:** Handling thousands of simultaneous user requests (logging in, searching, buying).",
                        "**Media Streaming:** Downloading video chunks in the background while the movie plays synchronously.",
                        "**High-Frequency Trading:** Processing thousands of stock trades per second using every available CPU core."
                    ],
                    codeProblems: [
                        {
                            title: "Concurrency Examples",
                            complexity: "Thread-Safe",
                            desc: "Patterns for safe parallel processing and state management.",
                            javaCode: `// Atomic Counter - O(1)
private AtomicInteger count = new AtomicInteger(0);
public void increment() { count.incrementAndGet(); }`,
                            pythonCode: `# Thread-Safe Counter
import threading
lock = threading.Lock()
count = 0
def increment():
    global count
    with lock: count += 1`
                        }
                    ]
                },
                {
                    title: "JVM Architecture & Memory Management",
                    category: "Java Stack",
                    desc: "The invisible engine that powers Java. It manages memory and ensures your code runs anywhere (Write Once, Run Anywhere).",
                    studyTips: "🧠 The JVM is like a smart house manager. You don't have to worry about cleaning up (Freeing memory); the manager handles it automatically (Garbage Collection).",
                    diagramType: "pyramid",
                    diagramData: {
                        levels: [
                            { label: "Stack (Fast / LIFO)", color: "#a855f7" },
                            { label: "Heap (Large / Dynamic)", color: "#6366f1" },
                            { label: "Metaspace (Blueprints)", color: "#3b82f6" }
                        ]
                    },
                    staticContent: [
                        "**Garbage Collection (GC):** Automatically finds and deletes objects that are no longer used by the program.",
                        "**Heap Memory:** The large playground where all your 'Objects' live. If it fills up, you get an OutOfMemory error.",
                        "**Stack Memory:** A small, super-fast area for temporary math and where individual functions keep their local variables.",
                        "**JIT (Just-In-Time):** A performance booster that translates Java into 'Machine Code' while the program is running.",
                        "**Write Once, Run Anywhere:** The JVM lets the same code run on Windows, Mac, or Linux without changes."
                    ],
                    realWorldUsage: [
                        "**Enterprise Servers:** Keeping large applications running for years without memory leaks.",
                        "**Cloud Computing:** Optimizing memory usage in Docker containers and Kubernetes pods to save costs.",
                        "**Android Development:** The foundation of how apps run on millions of different mobile devices."
                    ],
                    codeProblems: [
                        {
                            title: "Manual GC Hinting (Avoid This!)",
                            complexity: "O(JVM-Control)",
                            desc: "Understanding that we recommend the JVM, not force it.",
                            javaCode: `// This is like asking the trash collector to come early.
// He might say "no thanks".
System.gc(); `,
                            pythonCode: `import gc
# Force immediate garbage collection
gc.collect()`
                        }
                    ]
                },
                {
                    title: "Modern Java (Java 8 to 21)",
                    category: "Java Stack",
                    desc: "The evolution of Java into a faster, cleaner, and more powerful language. It's about writing less code that does more.",
                    studyTips: "🚀 Modern Java is like a sleek electric car vs the old diesel truck. It has powerful functional tools (Lambdas/Streams) and lightweight 'Virtual Threads'.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "Modern Productivity",
                        pillars: [
                            { label: "Streams", desc: "Functional Processing" },
                            { label: "Lambdas", desc: "Pass behavior" },
                            { label: "Records", desc: "Clean Data Classes" },
                            { label: "Virtual Threads", desc: "Scaling millions" }
                        ],
                        foundation: "Functional Interfaces"
                    },
                    staticContent: [
                        "**Streams API:** A way to process collections of data (filter, sort, transform) using a single, readable line of code.",
                        "**Lambdas:** Treating code blocks like variables so you can pass them into functions easily.",
                        "**Records:** Super-short classes for data storage. No more writing bulky getters, setters, or equals() methods.",
                        "**Virtual Threads (Java 21):** The biggest change in a decade. Allows your app to handle millions of tasks simultaneously with almost zero overhead.",
                        "**Switch Expressions:** A cleaner, safer way to handle multiple choices in your code."
                    ],
                    realWorldUsage: [
                        "**Data Processing:** Using Streams to filter and calculate totals for millions of transactions in real-time.",
                        "**Microservices:** Building high-performance APIs that can handle massive traffic using Java 21's Virtual Threads.",
                        "**Cloud Apps:** Reducing memory and startup time for apps running in AWS Lambda or Kubernetes."
                    ],
                    codeProblems: [
                        {
                            title: "Modern Java Examples",
                            complexity: "Functional",
                            desc: "Stream pipelines and functional transformations.",
                            javaCode: `// Stream API - O(N)
List<String> results = items.stream()
    .filter(s -> s.startsWith("A"))
    .map(String::toUpperCase)
    .collect(Collectors.toList());`,
                            pythonCode: `# List Comprehension - O(N)
results = [s.upper() for s in items if s.startswith("A")]`
                        }
                    ]
                },
                {
                    title: "Distributed Systems & Algorithms",
                    category: "Java Stack",
                    desc: "The architecture behind apps like Google and Netflix. It's about making many computers act as one single, reliable system.",
                    studyTips: "🌍 Distributed systems are hard because computers and networks fail. Master the patterns that keep things running when the 'inevitable' happens.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Load Balance" },
                            { label: "Service Mesh" },
                            { label: "Data Layer" },
                            { label: "Cache" },
                            { label: "Message Queue" }
                        ],
                        centerText: "Distributed"
                    },
                    staticContent: [
                        "**Fault Tolerance:** Building systems that don't die just because one server crashes.",
                        "**Eventual Consistency:** Accepting that data might take a few seconds to reach every server, as long as everyone eventually agrees.",
                        "**Idempotency:** Designing operations (like 'Withdraw $10') so they don't happen twice if a network hiccup retries the request.",
                        "**Circuit Breaker:** A safety switch that 'trips' and stops traffic if a service is failing, preventing a total system meltdown.",
                        "**Saga Pattern:** A way to handle complex tasks across multiple services without locking everything up."
                    ],
                    realWorldUsage: [
                        "**Global Databases:** Keeping your Instagram profile updated across servers in the USA, Europe, and Asia.",
                        "**Streaming Services:** Ensuring Netflix never buffers by distributing movies across thousands of 'Edge' servers globally.",
                        "**Payment Gateways:** Handling billions of transactions safely using retries and deduplication logic."
                    ]
                },
                {
                    title: "Agile & Software Development Lifecycle",
                    category: "Java Stack",
                    desc: "The 'Professional Way' to build apps. It's about working in small, fast cycles (Sprints) and getting constant feedback.",
                    studyTips: "🏃‍♂️ Agile isn't just about meetings. It's about being able to change direction quickly without wasting time. Think of it as 'Course Correction' for software.",
                    diagramType: "cycle",
                    diagramData: {
                        steps: [
                            { label: "Sprint Planning" },
                            { label: "Daily Standups" },
                            { label: "Sprint Execution" },
                            { label: "Sprint Review" },
                            { label: "Retrospective" }
                        ],
                        centerText: "Agile"
                    },
                    staticContent: [
                        "**Sprints:** 2-week bursts of focused work where a specific set of features are built and tested.",
                        "**Daily Standup:** A 15-minute sync where the team shares progress and identifies 'blockers'.",
                        "**Definition of Done:** A checklist ensuring code isn't 'done' until it's tested, reviewed, and ready for users.",
                        "**Backlog:** A prioritized 'To-Do' list of features and bug fixes.",
                        "**Retrospective:** A team meeting to discuss 'What went well?' and 'How can we improve?' for the next cycle."
                    ],
                    realWorldUsage: [
                        "**Large Tech Teams:** Companies like Amazon and Google use Agile to ship updates to millions of users every day.",
                        "**Startup Culture:** Rapidly building 'Minimum Viable Products' (MVPs) to test ideas with real users as fast as possible.",
                        "**Project Management:** Keeping complex projects on track by breaking them into manageable, 2-week pieces."
                    ]
                },
                {
                    title: "Behavioral Interview Questions (SWE)",
                    category: "Java Stack",
                    desc: "The 'Secret Weapon' for landing top-tier jobs. It's not about what you know, but how you've handled real-world challenges.",
                    studyTips: "🌟 Master the **STAR Method**: Situation, Task, Action, Result. It's the standard for professional interview storytelling.",
                    diagramType: "pillars",
                    diagramData: {
                        roof: "STAR Method",
                        pillars: [
                            { label: "Situation", desc: "Set context" },
                            { label: "Task", desc: "Your role" },
                            { label: "Action", desc: "What you did" },
                            { label: "Result", desc: "Measurable outcome" }
                        ],
                        foundation: "Be Specific & Quantify Impact"
                    },
                    staticContent: [
                        "**Handling Conflict:** 'Describe a time you disagreed with a teammate.' (Focus on professional resolution).",
                        "**Mistakes:** 'Tell me about a time you made a mistake.' (Focus on what you learned and how you fixed it).",
                        "**Ambiguity:** 'How do you handle vague requirements?' (Focus on asking clarifying questions and prototyping).",
                        "**Pressure:** 'Describe a time you had to meet a tight deadline.' (Focus on prioritization and focus).",
                        "**Success:** 'Tell me about your proudest technical achievement.' (Quantify the business impact)."
                    ],
                    realWorldUsage: [
                        "**FAANG Interviews:** Amazon, Google, and Meta use these questions to gauge 'Leadership Principles' and cultural fit.",
                        "**Promotion Cycles:** Using these storytelling techniques to justify career advancement to management.",
                        "**Team Building:** Better understanding how colleagues think and solve problems through shared experiences."
                    ]
                }
            ]
        }
    };

    const content = config[type];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const filteredTopics = content.topics.filter(topic => {
        const matchesSearch = topic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            topic.desc.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = activeCategory === 'All' || topic.category === activeCategory;
        return matchesSearch && matchesCategory;
    });

    const [activeLangs, setActiveLangs] = useState({}); // Tracking active lang per code problem index

    const handleTopicClick = (topic) => {
        if (topic.isExternalLink) {
            window.location.hash = topic.link;
            return;
        }
        setSelectedTopic(topic);
        setActiveLangs({}); // Reset when opening new topic
    };

    const toggleLang = (idx, lang) => {
        setActiveLangs(prev => ({ ...prev, [idx]: lang }));
    };

    const handleDeepDive = (topic, mode = 'interview') => {
        // Ensure topic is a full object as expected by AI component
        const topicObj = typeof topic === 'string' ? { title: topic } : topic;
        setActiveTopic({ ...topicObj, type });
        setSelectedTopic(null); // Close modal if open
        toggleCoach(true);
    };

    const handleOpenPlayground = (code, title) => {
        let finalCode = code;
        // Append execution call based on problem title for immediate feedback
        if (title === "Two Sum") {
            finalCode += "\n\n# Test Case\nnums = [2, 7, 11, 15]\ntarget = 9\nprint(f'Nums: {nums}, Target: {target}')\nprint(f'Result (Indices): {two_sum(nums, target)}')";
        } else if (title === "Valid Palindrome") {
            finalCode += "\n\n# Test Case\ns = \"A man, a plan, a canal: Panama\"\nprint(f'String: \"{s}\"')\nprint(f'Is Palindrome: {is_palindrome(s)}')";
        } else if (title === "Fibonacci (Iterative)") {
            finalCode += "\n\n# Test Case\nn = 10\nprint(f'Fibonacci of {n}: {fib(n)}')";
        } else if (title === "Prime Check") {
            finalCode += "\n\n# Test Case\nn = 29\nprint(f'Is {n} prime?: {is_prime(n)}')";
        } else if (title === "Valid Anagram") {
            finalCode += "\n\n# Test Case\ns1, s2 = \"anagram\", \"nagaram\"\nprint(f's1: {s1}, s2: {s2}')\nprint(f'Is Anagram: {is_anagram(s1, s2)}')";
        } else if (title === "Reverse String") {
            finalCode += "\n\n# Test Case\ns = [\"h\",\"e\",\"l\",\"l\",\"o\"]\nprint(f'Original: {s}')\nreverse_string(s)\nprint(f'Reversed: {s}')";
        } else if (title === "Binary Search") {
            finalCode += "\n\n# Test Case\nnums = [1, 3, 5, 7, 9, 11]\ntarget = 7\nprint(f'Nums: {nums}, Target: {target}')\nprint(f'Index: {binary_search(nums, target)}')";
        } else if (title === "Valid Parentheses (Stack)") {
            finalCode += "\n\n# Test Case\ns = \"()[]{}\"\nprint(f'Input: \"{s}\"')\nprint(f'Is Valid: {is_valid(s)}')";
        } else if (title === "Linked List (Reverse)") {
            finalCode += "\n\n# Test Case\nhead = ListNode(1, ListNode(2, ListNode(3)))\nprint('Original: 1 -> 2 -> 3')\nrev = reverse_list(head)\nprint(f'Reversed: {rev.val} -> {rev.next.val} -> {rev.next.next.val}')";
        } else if (title === "Quick Sort") {
            finalCode += "\n\n# Test Case\narr = [64, 34, 25, 12, 22, 11, 90]\nprint(f'Original: {arr}')\nprint(f'Sorted: {quick_sort(arr)}')";
        } else if (title === "Merge Sort") {
            finalCode += "\n\n# Test Case\narr = [64, 34, 25, 12, 22, 11, 90]\nprint(f'Original: {arr}')\nmerge_sort(arr)\nprint(f'Sorted: {arr}')";
        } else if (title === "Heap Sort") {
            finalCode += "\n\n# Test Case\narr = [64, 34, 25, 12, 22, 11, 90]\nprint(f'Original: {arr}')\nprint(f'Sorted: {heap_sort(arr)}')";
        } else if (title === "Bubble Sort") {
            finalCode += "\n\n# Test Case\narr = [64, 34, 25, 12, 22, 11, 90]\nprint(f'Original: {arr}')\nbubble_sort(arr)\nprint(f'Sorted: {arr}')";
        }

        setActivePlaygroundCode(finalCode);
    };

    const handleHeroStart = (topicName) => {
        // Create a temporary topic object for the featured module
        const topic = { title: topicName, category: 'Featured' };
        handleDeepDive(topic, 'interview');
    };

    const toggleQA = (id) => {
        setExpandedQA(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleQALang = (e, id, lang) => {
        e.stopPropagation();
        setQALangs(prev => ({ ...prev, [id]: lang }));
    };

    const toggleQASelection = (id) => {
        setSelectedQAs(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
                // Also expand when selected
                setExpandedQA(exp => ({ ...exp, [id]: true }));
            }
            return next;
        });
    };

    const handleExportCheatSheet = () => {
        console.log("Exporting cheat sheet...");
        if (selectedQAs.size === 0) {
            alert("Please select at least one question for your cheat sheet.");
            return;
        }

        let content = "# Java Interview Cheat Sheet\n\n";
        content += `Generated on: ${new Date().toLocaleDateString()}\n\n---\n\n`;

        javaQAData.forEach(cat => {
            const catSelected = cat.questions.filter(q => selectedQAs.has(q.id));
            if (catSelected.length > 0) {
                content += `## CATEGORY: ${cat.category.toUpperCase()}\n\n`;
                catSelected.forEach(q => {
                    content += `### Q: ${q.q}\n`;
                    const answer = q.concise || (q.a || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"');
                    content += `**A:** ${answer}\n\n`;

                    if (q.javaCode || q.pythonCode) {
                        const lang = qaLangs[q.id] || 'java';
                        const code = lang === 'python' ? (q.pythonCode || q.javaCode) : (q.javaCode || q.pythonCode);
                        if (code) {
                            content += `\`\`\`${lang}\n${code}\n\`\`\`\n\n`;
                        }
                    }
                    content += "---\n\n";
                });
            }
        });

        const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Java_Interview_Cheat_Sheet_${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    };

    const handlePrintCheatSheet = () => {
        if (selectedQAs.size === 0) {
            alert("Please select at least one question to print.");
            return;
        }

        const printWindow = window.open('', '_blank');
        let html = `
            <html>
            <head>
                <title>Java Interview Cheat Sheet</title>
                <style>
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
                    h1 { color: #6366f1; border-bottom: 2px solid #6366f1; padding-bottom: 10px; }
                    .category { margin-top: 30px; background: #f8fafc; padding: 10px; border-left: 5px solid #6366f1; }
                    .qa-item { margin-bottom: 25px; page-break-inside: avoid; border-bottom: 1px solid #e2e8f0; padding-bottom: 15px; }
                    .question { font-weight: bold; font-size: 1.1em; color: #1e293b; margin-bottom: 10px; }
                    .label { font-size: 0.8em; color: #6366f1; font-weight: bold; text-transform: uppercase; margin-right: 10px; }
                    .answer { margin-bottom: 15px; }
                    pre { background: #1e293b; color: #e2e8f0; padding: 15px; border-radius: 8px; overflow-x: auto; font-family: 'Consolas', monospace; font-size: 0.9em; }
                    .footer { font-size: 0.8em; color: #94a3b8; text-align: center; margin-top: 40px; }
                </style>
            </head>
            <body>
                <h1>Java Interview Cheat Sheet</h1>
                <p>Generated on: ${new Date().toLocaleDateString()}</p>
        `;

        javaQAData.forEach(cat => {
            const catSelected = cat.questions.filter(q => selectedQAs.has(q.id));
            if (catSelected.length > 0) {
                html += `<div class="category"><h2>${cat.category}</h2></div>`;
                catSelected.forEach(q => {
                    const lang = qaLangs[q.id] || 'java';
                    const code = lang === 'python' ? (q.pythonCode || q.javaCode) : (q.javaCode || q.pythonCode);
                    const answer = q.concise || q.a;
                    html += `
                        <div class="qa-item">
                            <div class="question"><span class="label">Q${q.id}</span> ${q.q}</div>
                            <div class="answer"><span class="label">Answer</span> ${answer}</div>
                            ${code ? `<div><span class="label">Code (${lang.toUpperCase()})</span><pre>${code}</pre></div>` : ''}
                        </div>
                    `;
                });
            }
        });

        html += `<div class="footer">Generated by Guru's Career Journey Q&A Hub</div></body></html>`;

        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.print();
        };
    };

    const renderQAHub = () => (
        <div className={`qa-hub-container ${selectionMode ? 'selection-mode' : ''}`}>
            <div className="qa-header">
                <div>
                    <h2>Java Interview Q&A Hub</h2>
                    <p>Master the top 80 Java questions compiled for high-tier technical interviews.</p>
                </div>
                <div className="qa-header-actions">
                    {selectionMode ? (
                        <>
                            <span className="selection-count">{selectedQAs.size} Selected</span>
                            <div className="qa-actions-group">
                                <button className="qa-action-btn primary" onClick={handlePrintCheatSheet}>
                                    🖨️ Print PDF
                                </button>
                                <button className="qa-action-btn primary" onClick={handleExportCheatSheet}>
                                    📄 Download MD
                                </button>
                                <button className="qa-action-btn secondary" onClick={() => {
                                    setSelectionMode(false);
                                    setSelectedQAs(new Set());
                                }}>
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <button className="qa-action-btn primary" onClick={() => setSelectionMode(true)}>
                            📝 Create Cheat Sheet
                        </button>
                    )}
                </div>
            </div>

            <div className="qa-categories">
                {javaQAData.map((cat, idx) => (
                    <div key={idx} className="qa-category-section">
                        <h3 className="qa-category-title">{cat.category}</h3>
                        <div className="qa-list">
                            {cat.questions.map((q) => (
                                <div
                                    key={q.id}
                                    className={`qa-card ${expandedQA[q.id] ? 'expanded' : ''} ${selectedQAs.has(q.id) ? 'selected' : ''} `}
                                    onClick={() => selectionMode ? toggleQASelection(q.id) : null}
                                >
                                    <div className="qa-question" onClick={(e) => {
                                        if (selectionMode) return;
                                        toggleQA(q.id);
                                    }}>
                                        <div className="qa-q-text">
                                            {selectionMode && (
                                                <div className={`qa-checkbox ${selectedQAs.has(q.id) ? 'checked' : ''} `}>
                                                    {selectedQAs.has(q.id) && '✓'}
                                                </div>
                                            )}
                                            <span className="qa-id">Q{q.id}.</span>
                                            {q.q}
                                        </div>
                                        <span className="qa-toggle-icon">{expandedQA[q.id] ? '−' : '+'}</span>
                                    </div>
                                    {expandedQA[q.id] && (
                                        <div className="qa-answer">
                                            <div className="qa-a-label">ANSWER</div>
                                            <div className="qa-a-text" dangerouslySetInnerHTML={{ __html: q.a }}></div>

                                            {(q.javaCode || q.pythonCode) && (
                                                <div className="qa-code-section">
                                                    <div className="qa-code-header">
                                                        <div className="qa-code-tabs">
                                                            <button
                                                                className={`qa-code-tab ${qaLangs[q.id] !== 'python' ? 'active' : ''} `}
                                                                onClick={(e) => toggleQALang(e, q.id, 'java')}
                                                            >
                                                                JAVA
                                                            </button>
                                                            <button
                                                                className={`qa-code-tab ${qaLangs[q.id] === 'python' ? 'active' : ''} `}
                                                                onClick={(e) => toggleQALang(e, q.id, 'python')}
                                                            >
                                                                PYTHON
                                                            </button>
                                                        </div>
                                                        {qaLangs[q.id] === 'python' && q.pythonCode && (
                                                            <button
                                                                className="qa-code-run"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenPlayground(q.pythonCode, q.q);
                                                                }}
                                                            >
                                                                ▶ Run
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="qa-code-block">
                                                        <pre><code>
                                                            {qaLangs[q.id] === 'python' ? (q.pythonCode || "// No Python sample") : (q.javaCode || "// No Java sample")}
                                                        </code></pre>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="qa-actions">
                                                <button
                                                    className="qa-ai-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeepDive({ title: q.q, category: cat.category }, 'explain');
                                                    }}
                                                >
                                                    🚀 Deep Dive with AI
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div >
    );

    const backArrow = (
        <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
        </svg>
    );

    const searchIcon = (
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );

    const practiceIcon = (
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
    );

    return (
        <div className="prof-page">
            <nav className="prof-nav">
                <a href="/" className="back-link">
                    {backArrow}
                    Back to Portfolio
                </a>
                <div className="prof-logo">{content.title} Central</div>
                <div className="prof-nav-actions">
                    <button className="nav-ai-btn" onClick={() => toggleCoach(true)}>
                        <AssistantLogo size="small" />
                        Assistant
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="prof-hero-section prof-wide-container">
                <div className="hero-text-col">
                    <h1>{content.title}</h1>
                    <p className="subtitle">{content.subtitle}</p>
                </div>
                <div className="hero-visual-col">
                    <HeroStats type={type} />
                </div>
            </section>

            {/* Tab Navigation for SWE */}
            {type === 'swe' && (
                <section className="main-tab-nav container">
                    <div className="tab-switcher">
                        <button
                            className={`tab-btn ${mainTab === 'modules' ? 'active' : ''}`}
                            onClick={() => setMainTab('modules')}
                        >
                            Modules
                        </button>
                        <button
                            className={`tab-btn ${mainTab === 'qa' ? 'active' : ''}`}
                            onClick={() => setMainTab('qa')}
                        >
                            Q&A Hub
                        </button>
                    </div>
                </section>
            )}

            {mainTab === 'modules' ? (
                <>
                    <section className="learning-intro container">
                        <div className="intro-card">
                            <div className="intro-icon">📚</div>
                            <div className="intro-text">
                                <h3>Interactive Learning Hub</h3>
                                <p>Welcome to your interview preparation center. Explore key industry frameworks, master core concepts, and deep-dive into interactive simulations. Each card provides actionable insights and potential interview context.</p>
                            </div>
                        </div>
                    </section>

                    <section className="search-filter-section container">
                        <div className="search-box-wrapper">
                            {searchIcon}
                            <input
                                type="text"
                                placeholder="Search concepts, keywords, or skills..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="category-filters">
                            {content.categories.map(cat => (
                                <button
                                    key={cat}
                                    className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
                                    onClick={() => setActiveCategory(cat)}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="prof-content prof-wide-container">
                        <div className="topics-grid">
                            {filteredTopics.length > 0 ? (
                                filteredTopics.map((topic, i) => (
                                    <div key={i} className="topic-card" onClick={() => handleTopicClick(topic)}>
                                        <span className="topic-category">{topic.category}</span>
                                        <h3>{topic.title}</h3>
                                        <p>{topic.desc}</p>
                                        <div className="card-footer">
                                            <div className="card-actions">
                                                <button
                                                    className="action-btn ai-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeepDive(topic, 'explain');
                                                    }}
                                                >
                                                    {practiceIcon} Explain
                                                </button>
                                                {topic.isExternalLink ? (
                                                    <div className="demo-badge">
                                                        Live Demo
                                                    </div>
                                                ) : topic.example ? (
                                                    <div className="example-badge">
                                                        Case Study
                                                    </div>
                                                ) : (
                                                    <div className="learn-badge">
                                                        Concept
                                                    </div>
                                                )}
                                            </div>
                                            <span className="arrow-more">
                                                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="no-results">No concepts found matching your search. Try a broader term.</p>
                            )}
                        </div>
                    </section>
                </>
            ) : (
                <section className="qa-hub-section container">
                    {renderQAHub()}
                </section>
            )}

            {selectedTopic && (
                <div className="topic-modal-overlay" onClick={() => setSelectedTopic(null)}>
                    <div className="topic-modal" onClick={e => e.stopPropagation()}>
                        <button className="close-modal" onClick={() => setSelectedTopic(null)}>&times;</button>
                        <div className="modal-header">
                            <span className="topic-category">{selectedTopic.category}</span>
                            <h2>{selectedTopic.title}</h2>
                        </div>

                        <div className="modal-body">
                            <div className="modal-section description">
                                <h4>Overview</h4>
                                <p>{selectedTopic.desc}</p>
                            </div>

                            {selectedTopic.hasAnimation ? (
                                <div className="modal-section">
                                    <InternetFlowAnimation />
                                </div>
                            ) : selectedTopic.hasLifecycleAnimation ? (
                                <div className="modal-section">
                                    <ProgramLifecycle />
                                </div>
                            ) : selectedTopic.hasSystemDesignLifecycle ? (
                                <div className="modal-section">
                                    <SystemDesignLifecycle />
                                </div>
                            ) : selectedTopic.diagramType ? (
                                <div className="modal-section">
                                    <Diagrams type={selectedTopic.diagramType} data={selectedTopic.diagramData} />
                                </div>
                            ) : (
                                selectedTopic.image && (
                                    <div className="modal-section topic-diagram-container">
                                        <img
                                            src={selectedTopic.image}
                                            alt={`${selectedTopic.title} Diagram`}
                                            className="topic-diagram"
                                        />
                                    </div>
                                )
                            )}

                            {selectedTopic.staticContent && (
                                <div className="modal-section static-content">
                                    <h4>Key Concepts</h4>
                                    <div className="static-box">
                                        <ul>
                                            {selectedTopic.staticContent.map((point, idx) => (
                                                <li key={idx} dangerouslySetInnerHTML={{ __html: point.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {selectedTopic.realWorldUsage && (
                                <div className="modal-section real-world-usage">
                                    <h4 style={{ color: '#6366f1' }}>Real-World Usage 💼</h4>
                                    <div className="usage-box" style={{ background: 'rgba(99, 102, 241, 0.05)', borderLeft: '3px solid #6366f1', padding: '1rem', borderRadius: '4px' }}>
                                        {Array.isArray(selectedTopic.realWorldUsage) ? (
                                            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                                                {selectedTopic.realWorldUsage.map((usage, idx) => (
                                                    <li key={idx} style={{ marginBottom: '0.5rem', color: '#94a3b8' }} dangerouslySetInnerHTML={{ __html: usage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p style={{ margin: 0, color: '#94a3b8' }}>{selectedTopic.realWorldUsage}</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedTopic.studyTips && (
                                <div className="modal-section study-tips">
                                    <h4>Professional Insights</h4>
                                    <div className="tips-box">
                                        <span className="tip-icon">💡</span>
                                        <p>{selectedTopic.studyTips}</p>
                                    </div>
                                </div>
                            )}

                            {selectedTopic.codeProblems && (
                                <div className="modal-section code-problems">
                                    <h4 className="section-subtitle">COMMON INTERVIEW PATTERNS</h4>
                                    <div className="code-problems-container">
                                        {selectedTopic.codeProblems.map((prob, idx) => (
                                            <div key={idx} className="code-problem-card">
                                                <div className="code-problem-header">
                                                    <span className="problem-title">{prob.title}</span>
                                                    <span className="problem-complexity pill">{prob.complexity}</span>
                                                </div>
                                                <p className="problem-desc">{prob.desc}</p>

                                                <div className="lang-tabs">
                                                    <button
                                                        className={`lang-btn java ${activeLangs[idx] !== 'python' ? 'active' : ''}`}
                                                        onClick={() => toggleLang(idx, 'java')}
                                                    >
                                                        JAVA
                                                    </button>
                                                    <button
                                                        className={`lang-btn python ${activeLangs[idx] === 'python' ? 'active' : ''}`}
                                                        onClick={() => toggleLang(idx, 'python')}
                                                    >
                                                        PYTHON
                                                    </button>
                                                </div>

                                                <div className="code-block">
                                                    <pre><code>{activeLangs[idx] === 'python' ? (prob.pythonCode || prob.code) : (prob.javaCode || "// No Java sample available")}</code></pre>
                                                </div>

                                                {activeLangs[idx] === 'python' && (
                                                    <button
                                                        className="open-playground-btn"
                                                        onClick={() => handleOpenPlayground(prob.pythonCode || prob.code, prob.title)}
                                                    >
                                                        ▶ Run Code
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedTopic.example && (
                                <div className="modal-section experience">
                                    <h4>Relevant My Experience</h4>
                                    <div className="example-box">
                                        <span className="exp-icon">★</span>
                                        <p>{selectedTopic.example}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            className="deep-dive-btn"
                            onClick={() => handleDeepDive(selectedTopic)}
                        >
                            Start Interactive Simulation
                        </button>
                    </div>
                </div>
            )}

            {activePlaygroundCode && (
                <PythonPlayground
                    initialCode={activePlaygroundCode}
                    onClose={() => setActivePlaygroundCode(null)}
                />
            )}

            <div className="footer-pusher"></div>
            <footer className="prof-footer">
                <div className="container footer-content">
                    <p>&copy; {new Date().getFullYear()} Guru. All Rights Reserved.</p>
                    <p className="footer-v">Empowering Career Excellence</p>
                </div>
            </footer>
        </div>
    );
};

export default ProfessionalPage;
